export const cPlusNotes = [
  {
    id: 1001,
    topic: "c++",
    seriesKey: "c++",
    title: "Introduction to C++",
    description:
      "Defining C++, detailing its history and key features, and setting up.",
    content: `
##  Introduction to C++
C++ is a **general-purpose, object-oriented programming language** developed by **Bjarne Stroustrup** at Bell Labs in **1983**. It was designed as an extension of the C language, originally called "C with Classes."
### History & Developer Info
* Developer: Bjarne Stroustrup
* Year:1983
* Origin: Bell Labs, evolved from the C language
### Key Features of C++
1. **Object-Oriented Programming (OOP):** Supports core OOP concepts like classes, inheritance, polymorphism, and encapsulation
2. **Multi-paradigm:** Supports procedural, object-oriented, and generic programming styles
3. **Standard Template Library (STL):** A vast library of ready-to-use containers, algorithms, and iterators
4. **Performance:** Provides high performance and low-level memory manipulation capabilities
##  Setting Up Environment
### Compilation and Execution
To compile and run C++ code, you need a compiler like **G++** (GNU C++ Compiler) or **Clang++**.
\`\`\`bash
# 1. Compile the program
g++ my_program.cpp -o my_app 

# 2. Run the compiled program
./my_app
\`\`\`
##  Simple "Hello, World!" Program
\`\`\`cpp
#include <iostream>  // Include input/output stream library
using namespace std; // Use standard namespace

int main() {         // Main function - program entry point
    cout << "Hello, World!" << endl;  // Output text to console
    return 0;        // Return 0 indicates successful execution
}
\`\`\`
`,
    tags: [
      "C++",
      "Introduction",
      "Bjarne Stroustrup",
      "OOP",
      "G++",
      "Clang++",
      "Hello World",
    ],
    date: "2024-10-01",
  },
  {
    id: 1002,
    topic: "c++",
    seriesKey: "c++",
    title: "Data Types, Variables, and Constants",
    description:
      "Understanding fundamental data types, variable declaration, and constants in C++.",
    content: `
##  Data Types in C++
C++ supports various built-in data types to store different kinds of data.
### Basic Data Types
| Type | Size | Description | Range |
|------|------|-------------|--------|
| \`int\` | 4 bytes | Integer numbers | -2³¹ to 2³¹-1 |
| \`float\` | 4 bytes | Single-precision floating point | 7 decimal digits |
| \`double\` | 8 bytes | Double-precision floating point | 15 decimal digits |
| \`char\` | 1 byte | Single character | -128 to 127 |
| \`bool\` | 1 byte | Boolean values | true/false |
##  Variables 
A variable is a named storage location in the computer's memory whose value can be changed during program execution.
### Variable Declaration
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    // Variable declaration and initialization
    int age = 25;           // Integer variable
    float price = 99.99f;   // Float variable
    double salary = 75000.50; // Double variable
    char grade = 'A';       // Character variable
    bool is_active = true;  // Boolean variable
    
    cout << "Age: " << age << endl;
    cout << "Price: " << price << endl;
    cout << "Salary: " << salary << endl;
    cout << "Grade: " << grade << endl;
    cout << "Active: " << is_active << endl;
    
    return 0;
}
\`\`\`
### Constants
The const keyword is the standard and preferred way to define type-safe constants in C++. It modifies a variable's declaration to make its value read-only.
\`\`\`cpp
#include <iostream>
using namespace std;

#define PI 3.14159  // Preprocessor constant

int main() {
    const int MAX_USERS = 100;  // const keyword constant
    const double GRAVITY = 9.81;
    
    cout << "PI: " << PI << endl;
    cout << "Max Users: " << MAX_USERS << endl;
    cout << "Gravity: " << GRAVITY << endl;
    
    // MAX_USERS = 200;  // This would cause compilation error
    
    return 0;
}
\`\`\`
### sizeof Operator
The sizeof operator is a fundamental unary operator in C++ (and C) that is used to determine the amount of memory, in bytes, occupied by a variable, a data type, or an expression's type.
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    cout << "Size of int: " << sizeof(int) << " bytes" << endl;
    cout << "Size of float: " << sizeof(float) << " bytes" << endl;
    cout << "Size of double: " << sizeof(double) << " bytes" << endl;
    cout << "Size of char: " << sizeof(char) << " bytes" << endl;
    cout << "Size of bool: " << sizeof(bool) << " bytes" << endl;
    
    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Data Types",
      "Variables",
      "Constants",
      "sizeof",
      "int",
      "float",
      "double",
      "char",
      "bool",
    ],
    date: "2024-10-02",
  },
  {
    id: 1003,
    topic: "c++",
    seriesKey: "c++",
    title: "(I/O) Operations in C++",
    description:
      "Handling standard input/output using cout, cin, and I/O manipulators.",
    content: `
##  Input/Output (I/O) in C++
C++ uses streams for I/O operations through the iostream library.
1.**The iostream Library** 
This header provides the essential facilities for handling standard input and output operations:
- std::cout (Console Output Stream): Used to send data to the standard output device (usually the screen).
- std::cin (Console Input Stream): Used to read data from the standard input device (usually the keyboard).
- std::cerr (Console Error Stream): Used for unbuffered error output.
- std::clog (Console Log Stream): Used for buffered log messages.
2.**Operator**
- Insertion Operator (<<): Used with cout to "insert" - data into the output stream.
- Extraction Operator (>>): Used with cin to "extract" data from the input stream.
### Basic I/O Operations
\`\`\`cpp
#include <iostream>
#include <iomanip>  // For I/O manipulators
using namespace std;

int main() {
    int age;
    string name;
    double salary;
    
    // Output using cout
    cout << "Enter your name: ";
    cin >> name;                    // Input using cin
    
    cout << "Enter your age: ";
    cin >> age;
    
    cout << "Enter your salary: ";
    cin >> salary;
    
    // Formatted output
    cout << "\\n--- User Information ---" << endl;
    cout << "Name: " << name << endl;
    cout << "Age: " << age << endl;
    cout << fixed << setprecision(2);  // Set decimal precision
    cout << "Salary: $" << salary << endl;
    
    return 0;
}
\`\`\`
### String Input with Spaces
- In C++, reading string input that contains spaces requires using a specific function called std::getline() because the standard extraction operator.
- In C++ cin >> is designed to stop reading as soon as it encounters any whitespace (a space, tab, or newline).
\`\`\`cpp
#include <iostream>
#include <string>   // For string class
using namespace std;

int main() {
    string full_name;
    string address;
    
    cout << "Enter your full name: ";
    cin.ignore();  // Clear input buffer
    getline(cin, full_name);  // Read entire line with spaces
    
    cout << "Enter your address: ";
    getline(cin, address);
    
    cout << "\\nFull Name: " << full_name << endl;
    cout << "Address: " << address << endl;
    
    return 0;
}
\`\`\`
### I/O Manipulators
I/O Manipulators in C++ are special functions or objects used to control the format and state of I/O streams (like std::cout and std::cin).
\`\`\`cpp
#include <iostream>
#include <iomanip>
using namespace std;

int main() {
    double number = 123.456789;
    
    cout << "Default: " << number << endl;
    cout << fixed << setprecision(2) << "Fixed 2 decimal: " << number << endl;
    cout << scientific << "Scientific: " << number << endl;
    
    cout << setw(15) << left << "Left aligned" << endl;
    cout << setw(15) << right << "Right aligned" << endl;
    
    cout << setfill('*') << setw(20) << "Filled" << endl;
    
    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Input Output",
      "cout",
      "cin",
      "I/O Manipulators",
      "getline",
      "iomanip",
    ],
    date: "2024-10-03",
  },
  {
    id: 1004,
    topic: "c++",
    seriesKey: "c++",
    title: "Operators in C++",
    description:
      "Understanding different types of operators and their precedence in C++.",
    content: `
##  Operators in C++
Operators perform operations on variables and values. C++ has various types of operators.
### Arithmetic Operators
These are those operators perform mathematical calculations on numerical data.

 | Operator | Name | Function | Example | Result
|------|------|-------------|--------|------- |
| \`+\` | Addition | Sums two operands | 7+5 | 10
| \`-\` | Subtraction | Finds the difference between two operands. | 7 - 3 | 4
| \`*\` | Multiplication | Multiplies two operands. | 7 * 3 | 21
| \`/\` | Division | Divides the first by the second. | 7 / 3| 2
| \`%\` | Modulo | Finds the remainder of an integer division. |7 % 3 | 1

\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    int a = 15, b = 4;
    
    cout << "a = " << a << ", b = " << b << endl;
    cout << "Addition (a + b): " << a + b << endl;
    cout << "Subtraction (a - b): " << a - b << endl;
    cout << "Multiplication (a * b): " << a * b << endl;
    cout << "Division (a / b): " << a / b << endl;
    cout << "Modulus (a % b): " << a % b << endl;
    
    // Increment and Decrement
    int x = 5;
    cout << "x = " << x << endl;
    cout << "Post-increment (x++): " << x++ << endl;  // Use then increment
    cout << "After post-increment: " << x << endl;
    cout << "Pre-increment (++x): " << ++x << endl;   // Increment then use
    
    return 0;
}
\`\`\`
### Relational and Logical Operators
These operators are used to perform comparisons and combine boolean conditions (true or false). They are foundational for decision-making and control flow (like if statements and loops).
**1.Relational Operators**
Relational operators compare two values and return a Boolean result: true (usually represented as 1) or false (represented as 0).
 | Operator | Name | Example Code | Result | 
|------|------|-------------|--------|
| \`==\` | Equal To |  a == b ( i.e., 10 == 5) | false | 
| \`!=\` | Not Equal To | a != b ( i.e., 10 != 5) | true |
| \`>\` | Greater Than | a > b ( i.e., 10 > 5) | true | 
| \`<\` | Less Than | a < b ( i.e., 10 < 5) | false | 
| \`>=\` | Greater Than or Equal To | a >= b ( i.e., 10 >= 5) | true | 
** 2.Logical Operators **
These operators are used to combine two or more relational (or boolean) expressions or to negate a condition. They also return a Boolean result (true or false).
  | Name | Purpose | 
|------|-------------|
| Logical AND |  Returns true only if both operands are true. |  
| Logical OR | Returns true if any of operands are true. |
| Logical NOT | Reverses the logical state of its operand. | 
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    int num1 = 10, num2 = 20;
    bool result;
    
    // Relational Operators
    cout << num1 << " == " << num2 << " : " << (num1 == num2) << endl;
    cout << num1 << " != " << num2 << " : " << (num1 != num2) << endl;
    cout << num1 << " > " << num2 << " : " << (num1 > num2) << endl;
    cout << num1 << " < " << num2 << " : " << (num1 < num2) << endl;
    cout << num1 << " >= " << num2 << " : " << (num1 >= num2) << endl;
    cout << num1 << " <= " << num2 << " : " << (num1 <= num2) << endl;
    
    // Logical Operators
    bool isSunny = true, isWarm = false;
    cout << "isSunny: " << isSunny << ", isWarm: " << isWarm << endl;
    cout << "AND (isSunny && isWarm): " << (isSunny && isWarm) << endl;
    cout << "OR (isSunny || isWarm): " << (isSunny || isWarm) << endl;
    cout << "NOT (!isSunny): " << (!isSunny) << endl;
    
    return 0;
}
\`\`\`
### Assignment and Compound Operators
These operators are used to assign a value to a variable, often performing an arithmetic operation first.
**1.Assignment Operator **:The Assignment Operator (=) is used to store the value of the right-hand operand into the variable on the left-hand side. 
Example : int a = 20 ; // Assign value to a variable.
**2.Compound Operators ** : These operators combine an arithmetic operation with the assignment operation. They provide a shorthand way to perform an operation on a variable and then store the new result back into the same variable.
Example : int a +=5; //Increases the value of a by 5.
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    int x = 10;
    
    cout << "Initial x: " << x << endl;
    
    x += 5;  // x = x + 5
    cout << "After x += 5: " << x << endl;
    
    x -= 3;  // x = x - 3
    cout << "After x -= 3: " << x << endl;
    
    x *= 2;  // x = x * 2
    cout << "After x *= 2: " << x << endl;
    
    x /= 4;  // x = x / 4
    cout << "After x /= 4: " << x << endl;
    
    x %= 3;  // x = x % 3
    cout << "After x %= 3: " << x << endl;
    
    return 0;
}
\`\`\`
### Operator Precedence
Operator precedence defines the order in which operators are evaluated in an expression when there are multiple operators. This is similar to the "order of operations" in mathematics (PEMDAS/BODMAS)
 P = Parentheses , B = Brackets, E = Exponents, O=Orders, M= Multiplication, D = Division, A = Addition, S = Subtraction).
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    int result1 = 5 + 3 * 2;     // Multiplication first: 5 + 6 = 11
    int result2 = (5 + 3) * 2;   // Parentheses first: 8 * 2 = 16
    
    cout << "5 + 3 * 2 = " << result1 << endl;
    cout << "(5 + 3) * 2 = " << result2 << endl;
    
    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Operators",
      "Arithmetic",
      "Relational",
      "Logical",
      "Assignment",
      "Precedence",
    ],
    date: "2024-10-04",
  },
  {
    id: 1005,
    topic: "c++",
    seriesKey: "c++",
    title: "Control Flow",
    description:
      "Controlling program execution flow using if/else, switch, and loops.",
    content: `
##  Control Flow in C++
Control flow structures dictate the order in which instructions are executed.
### Conditional Statements
** 1. if Statement : **This is the most basic structure. The code block executes only if the condition is true. If the condition is false, the program simply skips the block and continues.
- Syntax
\`\`\`cpp
if (condition) {
    // Executes ONLY if 'condition' is true
}
// Program continues here
\`\`\`
** 2. if-else Statement : **This structure provides a choice between two actions. One of the two blocks is guaranteed to execute.
- Syntax
\`\`\`cpp
if (condition) {
    // Executes if the 'condition' is true
} else {
    // Executes if the 'condition' is false
}
\`\`\`
**3.if-else if-else Statement : ** This structure allows you to test a series of conditions. The program checks conditions sequentially until it finds the first one that is true, executes that block, and then exits the entire chain.
- Syntax
\`\`\`cpp
if (condition1) {
    // Executes if condition1 is true
} else if (condition2) {
    // Executes if condition1 is false, BUT condition2 is true
} else if (condition3) {
    // Executes if condition1 and condition2 are false, BUT condition3 is true
} else {
    // Executes ONLY if ALL preceding conditions are false
}
\`\`\`

 - Simple program which demonstrate the use of conditional statements.
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    int number;
    
    cout << "Enter a number: ";
    cin >> number;
    
    // Simple if statement
    if (number > 0) {
        cout << "The number is positive." << endl;
    }
    
    // if-else statement
    if (number % 2 == 0) {
        cout << "The number is even." << endl;
    } else {
        cout << "The number is odd." << endl;
    }
    
    // if-else if ladder
    if (number > 100) {
        cout << "The number is greater than 100." << endl;
    } else if (number > 50) {
        cout << "The number is between 51 and 100." << endl;
    } else if (number > 0) {
        cout << "The number is between 1 and 50." << endl;
    } else {
        cout << "The number is zero or negative." << endl;
    }
    
    return 0;
}
\`\`\`

### Switch Statement
The switch statement evaluates a single expression (usually an integer or an enumerated type) and then compares that result against several constant values, known as case labels. If a match is found, the corresponding block of code is executed.
- Syntax
\`\`\`cpp
switch (expression) {
    case constant_value_1:
        // Code to execute if expression == constant_value_1
        break; // Crucial: Exits the switch block

    case constant_value_2:
    case constant_value_3:
        // Code to execute if expression == constant_value_2 OR constant_value_3
        break;

    default:
        // Code to execute if NONE of the case labels match
        // break; (Optional here, as it's the last statement)
}
\`\`\`
### Key terms of switch statement
** 1.Expression : ** The single variable or value (must be an integer type like int, char, or enum) that the switch statement evaluates and compares against all case labels.
** 2. case : ** A label followed by a constant value. If the switch expression matches this constant value, the code following the colon (:) is executed.
** 3. break : ** A crucial keyword used to immediately terminate the switch statement, preventing code execution from "falling through" to the next case label.
** 4. default : ** An optional catch-all label. The code under default executes only if the switch expression does not match any of the preceding case constant values.

- A simple program which demonstrate the working of switch statement.
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    int day;
    
    cout << "Enter day number (1-7): ";
    cin >> day;
    
    switch (day) {
        case 1:
            cout << "Monday" << endl;
            break;
        case 2:
            cout << "Tuesday" << endl;
            break;
        case 3:
            cout << "Wednesday" << endl;
            break;
        case 4:
            cout << "Thursday" << endl;
            break;
        case 5:
            cout << "Friday" << endl;
            break;
        case 6:
            cout << "Saturday" << endl;
            break;
        case 7:
            cout << "Sunday" << endl;
            break;
        default:
            cout << "Invalid day number!" << endl;
    }
    
    return 0;
}
\`\`\`
### Loops in C++
A loop is a control flow statement that executes a block of code repeatedly as long as a specified condition remains true.
### Types of Loops
C++ provides three primary looping constructs, each suited for different scenarios:
** 1. for Loop (Count-Controlled Loop) : **Used when the number of iterations is known before the loop starts.
- Syntax
 \`\`\`cpp
for (initialization; condition; update) {
    // code to repeat
}
 \`\`\`
 ** 2.while Loop (Condition-Controlled Loop) : **Used when the number of iterations is not known beforehand. The loop continues to execute as long as the condition remains true.
 - Syntax 
 \`\`\`cpp
while (condition) {
    // code to repeat
    // MUST include an update to change the condition eventually
}
  \`\`\`
  ** 3. do-while Loop (Post-Test Loop): ** Similar to the while loop, but the condition is checked after the loop body runs. This guarantees that the loop body will execute at least once, regardless of the initial condition.
  - Syntax
  \`\`\`cpp
do {
    // code to repeat
} while (condition); // Semicolon is mandatory here
    \`\`\`
   - A simple program which demonstrate the working of the looping statements.
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    // For loop - when iterations are known
    cout << "For loop (1 to 5): ";
    for (int i = 1; i <= 5; i++) {
        cout << i << " ";
    }
    cout << endl;
    
    // While loop - condition checked before execution
    cout << "While loop (5 to 1): ";
    int j = 5;
    while (j >= 1) {
        cout << j << " ";
        j--;
    }
    cout << endl;
    
    // Do-while loop - executes at least once
    cout << "Do-while loop: ";
    int k = 10;
    do {
        cout << k << " ";
        k++;
    } while (k < 5);  // Condition fails but loop runs once
    cout << endl;
    
    return 0;
}
\`\`\`

### Break and Continue
Break and continue are jump statements in C++ used to alter the normal flow of control within loops.
###  break Statement
It is used to immediately terminate the innermost loop (like for, while, or do-while) or a switch statement it is contained within.
-Syntax
\`\`\`cpp
break;
\`\`\`
###  continue Statement
It is used to skip the remaining statements within the current iteration of a loop (for, while, or do-while) and immediately proceeds to the next iteration of the loop.
-Syntax
\`\`\`cpp
continue;
\`\`\`
- A simple program which demonstrate the working of the break and continue statements.
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    // Break example - exit loop immediately
    cout << "Break example (stops at 5): ";
    for (int i = 1; i <= 10; i++) {
        if (i == 6) {
            break;  // Exit loop when i equals 6
        }
        cout << i << " ";
    }
    cout << endl;
    
    // Continue example - skip current iteration
    cout << "Continue example (skips even numbers): ";
    for (int i = 1; i <= 10; i++) {
        if (i % 2 == 0) {
            continue;  // Skip even numbers
        }
        cout << i << " ";
    }
    cout << endl;
    
    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Control Flow",
      "if-else",
      "switch",
      "for loop",
      "while loop",
      "do-while",
      "break",
      "continue",
    ],
    date: "2024-10-05",
  },
  {
    id: 1006,
    topic: "c++",
    seriesKey: "c++",
    title: "Function",
    description:
      "Structuring code using functions, parameters, return values, and function overloading.",
    content: `
##  Functions in C++
A function is a block of code designed to perform a specific task, promoting code reuse and modularity.
Functions are used to break down a large program into smaller, manageable, and reusable pieces.
### Function Header
The header specifies how the function is used and includes:
** 1.Return Type: **The data type of the value the function returns after execution (e.g., int, double, void). If the function doesn't return a value, the return type is void.
** 2.Function Name: ** A unique identifier for the function.
** 3.Parameter List (or Argument List):**A list of variables and their types that the function accepts as input, enclosed in parentheses (). The list can be empty.
### Function Body
The body contains the set of instructions (statements) that are executed when the function is called. These instructions are enclosed in curly braces {}.
- Syntax
\`\`\`cpp
returnType functionName(parameterType1 parameterName1, parameterType2 parameterName2, ...) {
    // Function body: statements to be executed
    // ...
    // Optional: return statement (if returnType is not void)
    return expression;
}
\`\`\`
-  Example 
\`\`\`cpp
#include <iostream>
using namespace std;

// Function declaration (prototype)
int add(int a, int b);

// Function definition
int add(int a, int b) {
    return a + b;
}

// Function with no return value (void)
void greet(string name) {
    cout << "Hello, " << name << "!" << endl;
}

// Function with default arguments
double calculateArea(double length, double width = 1.0) {
    return length * width;
}

int main() {
    // Function calls
    int result = add(5, 3);
    cout << "5 + 3 = " << result << endl;
    
    greet("Alice");
    
    double area1 = calculateArea(5.0, 3.0);  // Uses both parameters
    double area2 = calculateArea(5.0);       // Uses default width
    
    cout << "Area with both parameters: " << area1 << endl;
    cout << "Area with default width: " << area2 << endl;
    
    return 0;
}
\`\`\`

### Types of Functions
Functions in C++ are primarily categorized into two main types:
** 1.Standard Library Functions (Predefined Functions)**
These are functions that are already built into the C++ compiler and stored in header files (e.g., <iostream>, <cmath>, <string>). You can use them directly by including the appropriate header file.
- Examples: 
**1.std::cout (from <iostream>) for output.**
**2.std::cin (from <iostream>) for input.**
**3.sqrt() (from <cmath>) for calculating the square root.**
**4.sin() (from <cmath>) for calculating the sine of an angle.**
\`\`\`cpp
#include <iostream>
using namespace std;

// Overloaded functions - same name, different parameters
int multiply(int a, int b) {
    return a * b;
}

double multiply(double a, double b) {
    return a * b;
}

int multiply(int a, int b, int c) {
    return a * b * c;
}

int main() {
    cout << "Multiply integers: " << multiply(4, 5) << endl;
    cout << "Multiply doubles: " << multiply(4.5, 2.5) << endl;
    cout << "Multiply three integers: " << multiply(2, 3, 4) << endl;
    
    return 0;
}
\`\`\`
**2.User-Defined Functions **
These are functions created by the programmer to perform specific tasks as required by the program logic.
- Categories of User-Defined Functions:
 **1.Functions with no arguments and no return value:** The function performs a task without needing any input and doesn't return a result to the caller.
 **2.Functions with arguments but no return value:**The function accepts input (arguments) to perform its task but doesn't return a result.
 **3.Functions with no arguments but a return value:**The function performs a task (e.g., reads input from the user) and returns a single result, but doesn't take input arguments.
 **4.Functions with arguments and a return value:**The function accepts input (arguments) and performs a task, then returns a result to the caller. This is the most common type.
 ### OtherImportant C++ Function Concepts
 Beyond the basic types, functions are also categorized based on their usage and declaration:
 **1.Inline Functions:**A hint to the compiler to replace the function call with the actual function body at the compile time. This is often used for small, frequently called functions to reduce the overhead of function calls.
 \`\`\`cpp
 #include <iostream>

// The 'inline' keyword is a request to the compiler, not a command.
// Compilers often ignore it for large functions.
inline int product(int a, int b) {
    // This is a simple, small function, perfect for inlining.
    return a * b;
}

int main() {
    int x = 10;
    int y = 5;

    // When the compiler processes this line, it might replace the
    // function call with the actual code: result = 10 * 5;
    int result = product(x, y);

    std::cout << "The product is: " << result << std::endl;

    return 0;
}
 \`\`\`
 **2.Recursive Functions:**A function that calls itself, either directly or indirectly, to solve a problem by breaking it down into smaller, similar subproblems.
  \`\`\`cpp
   #include <iostream>

// Function to calculate the factorial of a non-negative integer 'n'.
// Factorial of 5 (5!) is 5 * 4 * 3 * 2 * 1 = 120.
long long factorial(int n) {
    // 1. Base Case: The condition that stops the recursion.
    // Without a base case, the function would call itself infinitely (stack overflow).
    if (n <= 1) {
        return 1;
    }

    // 2. Recursive Step: The function calls itself with a smaller input (n-1).
    // The current call waits for the result of the call with (n-1).
    return n * factorial(n - 1);
}

int main() {
    int num = 5;
    
    std::cout << "Factorial of " << num << " is: " << factorial(num) << std::endl;
    
    // How it works:
    // factorial(5) returns 5 * factorial(4)
    // factorial(4) returns 4 * factorial(3)
    // ...
    // factorial(1) returns 1 (Base Case)

    return 0;
}
   \`\`\`
 **3.Member Functions (Methods):** Functions defined inside a class. They operate on the data (attributes) of the class object.
  \`\`\`cpp
#include <iostream>
#include <string>

// Define a class named 'Car'
class Car {
private:
    std::string model;
    int year;

public:
    // 1. Constructor: A special member function to initialize object data.
    Car(std::string m, int y) {
        model = m;
        year = y;
    }

    // 2. Member Function (Method): A function inside the class.
    // It has direct access to the private data members (model and year).
    void displayInfo() {
        std::cout << "Car Model: " << model << std::endl;
        std::cout << "Year: " << year << std::endl;
    }

    // 3. Another Member Function: Performs an action.
    void startEngine() {
        std::cout << model << "'s engine is starting..." << std::endl;
    }
};

int main() {
    // Create an object (instance) of the Car class
    Car myCar("Toyota Camry", 2022);

    // Call the member functions using the object and the dot operator (.)
    myCar.displayInfo();
    myCar.startEngine();

    return 0;
}
    \`\`\`

 **4.Friend Functions:** Functions that are not members of a class but are granted special permission to access the private and protected members of that class.
 \`\`\`cpp
#include <iostream>

class Box {
private:
    int length;
    int width;

    // Private member function
    int calculateArea() const {
        return length * width;
    }

public:
    Box(int l, int w) : length(l), width(w) {}

    // DECLARATION of the friend function inside the class.
    // This grants it access to private members (length, width, calculateArea).
    friend void printBoxDetails(const Box& b);
};

// DEFINITION of the friend function outside the class.
// It is NOT called using a dot operator (b.printBoxDetails()), but as a regular function.
void printBoxDetails(const Box& b) {
    // The friend function can access private members directly:
    std::cout << "Box Length (Private): " << b.length << std::endl;
    std::cout << "Box Width (Private): " << b.width << std::endl;
    
    // It can also call private member functions:
    std::cout << "Box Area (via Private fn): " << b.calculateArea() << std::endl;
}

int main() {
    Box myBox(10, 5);

    // Call the friend function like a normal, standalone function.
    printBoxDetails(myBox);

    return 0;
}
  \`\`\`
 ** 5.Virtual Functions:**Member functions in a base class that you expect to be redefined in derived classes. They are crucial for achieving polymorphism at runtime.
\`\`\`cpp
#include <iostream>
#include <string>

// Base Class
class Animal {
public:
    std::string name;

    Animal(const std::string& n) : name(n) {}

    // The 'virtual' keyword makes this function a virtual function.
    // It tells the compiler to look up the correct function implementation 
    // at runtime based on the actual object type, not the pointer type.
    virtual void speak() const {
        std::cout << name << " makes a generic animal sound." << std::endl;
    }

    // A virtual destructor is usually required in base classes 
    // to ensure proper cleanup of derived class objects.
    virtual ~Animal() = default; 
};

// Derived Class 1
class Dog : public Animal {
public:
    Dog(const std::string& n) : Animal(n) {}

    // The 'override' keyword is optional but highly recommended for clarity and error-checking.
    void speak() const override {
        std::cout << name << " barks: Woof! Woof!" << std::endl;
    }
};

// Derived Class 2
class Cat : public Animal {
public:
    Cat(const std::string& n) : Animal(n) {}

    void speak() const override {
        std::cout << name << " meows: Meow!" << std::endl;
    }
};

int main() {
    // 1. Create a Base Class POINTER.
    // This is the key to runtime polymorphism.
    Animal* myAnimal;

    // 2. Point the base pointer to a Derived Class (Dog) object.
    myAnimal = new Dog("Buddy");

    // Runtime Polymorphism in action:
    // Even though myAnimal is an 'Animal*' pointer, the 'virtual' keyword ensures
    // the Dog's speak() function is called. This is Dynamic Binding.
    std::cout << "--- Calling Dog's speak() ---" << std::endl;
    myAnimal->speak(); 
    delete myAnimal; // Cleanup

    std::cout << "\n--- Calling Cat's speak() ---" << std::endl;
    // 3. Point the same base pointer to another Derived Class (Cat) object.
    myAnimal = new Cat("Whiskers");
    myAnimal->speak(); // Now the Cat's speak() is called.
    delete myAnimal; // Cleanup

    return 0;
}
  \`\`\`

`,
    tags: [
      "C++",
      "Functions",
      "Overloading",
      "Recursion",
      "Inline",
      "Default Arguments",
      "Prototype",
    ],
    date: "2024-10-06",
  },
  {
    id: 1007,
    topic: "c++",
    seriesKey: "c++",
    title: "Arrays and Strings in C++",
    description:
      "Working with single/multi-dimensional arrays and string manipulation.",
    content: `
##  Arrays in C++
Arrays are collections of elements of the same type stored in contiguous memory locations.
### Key Characteristics
- Fixed Size: Once an array is declared, its size cannot be changed.
- Homogeneous: All elements in an array must be of the same data type (e.g., all int, all double, or all string).
**Syntax ** : The general syntax for declaring an array in C++ is:
\`\`\`cpp
dataType arrayName[arraySize];
\`\`\`
### Types of Arrays
Arrays are primarily categorized based on their dimensionality:
**1.One-Dimensional Arrays (1D)**
A one-dimensional array (or vector) is the simplest form of an array. It represents a single list of data.
- Definition : A linear sequence of elements of the same type.
**Syntax:**
\`\`\`cpp
dataType arrayName[size];
\`\`\`
**Example:**
\`\`\`cpp
int ages[4] = {25, 30, 22, 28};// Accessing the third element (index 2): ages[2]
\`\`\`
**2.Multi-Dimensional Arrays**
Arrays with two or more dimensions. The most common is the two-dimensional array.
**Two-Dimensional Arrays (2D):** An array of arrays, often visualized as a grid or a table with rows and columns.
**Syntax:**
\`\`\`cpp
dataType arrayName[rows][columns];
\`\`\`
**Example:**
\`\`\`cpp
int matrix[2][3] = {
    {1, 2, 3},  // Row 0
    {4, 5, 6}   // Row 1
};// Accessing the element at Row 1, Column 0: matrix[1][0] which is 4
\`\`\`
**Three-Dimensional Arrays (3D):** An array of 2D arrays, visualized as a cube or a stack of tables.
** Syntax:**
\`\`\`cpp
dataType arrayName[depth][rows][columns];
\`\`\`
### Simple Program Example (1D Array)
\`\`\`cpp
#include <iostream>

int main() {
    // 1. Array Declaration and Initialization
    // 'grades' is an integer array capable of holding 5 elements.
    int grades[5] = {85, 90, 78, 92, 88};

    int sum = 0;
    
    // 2. Accessing and Iterating Array Elements
    std::cout << "The grades are: ";
    
    // Loop through the array from index 0 up to (but not including) 5.
    for (int i = 0; i < 5; ++i) {
        // Access element using the index 'i'
        std::cout << grades[i] << " ";
        sum += grades[i];
    }
    
    // 3. Calculation
    double average = (double)sum / 5;
    
    std::cout << "\n-----------------------" << std::endl;
    std::cout << "Total sum of grades: " << sum << std::endl;
    std::cout << "Average grade: " << average << std::endl;

    return 0;
}
\`\`\`
**Output:**
\`\`\`cpp
The grades are: 85 90 78 92 88 
-----------------------
Total sum of grades: 433
Average grade: 86.6
\`\`\`

### Multi-dimensional Arrays
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    // 2D array declaration and initialization
    int matrix[3][3] = {
        {1, 2, 3},
        {4, 5, 6},
        {7, 8, 9}
    };
    
    // Displaying 2D array
    cout << "2D Array elements:" << endl;
    for (int i = 0; i < 3; i++) {
        for (int j = 0; j < 3; j++) {
            cout << matrix[i][j] << " ";
        }
        cout << endl;
    }
    
    return 0;
}
\`\`\`
**Output:**
\`\`\`cpp
2D Array elements:
1 2 3 
4 5 6 
7 8 9
\`\`\`
##  Strings in C++
A string in C++ is a sequence of characters. It's used to store and manipulate text data, such as names, sentences, or paragraphs.
** In C++, there are two primary ways to handle strings:**
### C-style Strings (Character Arrays)
**C-Style String (Character Array) :**An array of characters terminated by a null character (\0). This is inherited from the C language.
\`\`\`cpp
#include <iostream>
#include <cstring>  // For C-string functions
using namespace std;

int main() {
    // C-style string declaration
    char greeting1[6] = {'H', 'e', 'l', 'l', 'o', '\\0'};
    char greeting2[] = "Hello World";
    char name[20];
    
    cout << "Greeting 1: " << greeting1 << endl;
    cout << "Greeting 2: " << greeting2 << endl;
    
    // String functions
    cout << "Length of greeting2: " << strlen(greeting2) << endl;
    
    strcpy(name, "Alice");  // Copy string
    cout << "Name: " << name << endl;
    
    strcat(name, " Smith");  // Concatenate strings
    cout << "Full Name: " << name << endl;
    
    // String comparison
    char str1[] = "apple";
    char str2[] = "banana";
    
    if (strcmp(str1, str2) < 0) {
        cout << str1 << " comes before " << str2 << endl;
    }
    
    return 0;
}
\`\`\`
### std::string Class
**std::string (C++ Standard Library):  **A more robust, flexible, and safer class provided by the C++ Standard Library (requires including 
the string header). This is the preferred way to work with strings in modern C++.
\`\`\`cpp
#include <iostream>
#include <string>  // For std::string
using namespace std;

int main() {
    // String object declaration
    string str1 = "Hello";
    string str2 = "World";
    string str3;
    
    // String concatenation
    str3 = str1 + " " + str2;
    cout << "Concatenated string: " << str3 << endl;
    
    // String length
    cout << "Length: " << str3.length() << endl;
    
    // String comparison
    if (str1 == str2) {
        cout << "Strings are equal" << endl;
    } else {
        cout << "Strings are different" << endl;
    }
    
    // Substring
    string substring = str3.substr(0, 5);
    cout << "Substring: " << substring << endl;
    
    // Find
    size_t pos = str3.find("World");
    if (pos != string::npos) {
        cout << "'World' found at position: " << pos << endl;
    }
    
    return 0;
}
\`\`\`
### Key Characteristics of std::string
** 1.Dynamic Size: **Unlike C-style arrays, std::string can grow or shrink in size dynamically.
** 2.Easy Concatenation: **Strings can be easily joined using the + operator.
** 3.Built-in Functions: ** Provides many useful methods (like length(), append(), find()) for manipulation.
**Simple C++ Program which demonstrate the working of the string **
\`\`\`cpp
#include <iostream>
#include <string> // Required header for std::string
using namespace std;

int main() {
    // 1. Declaration and Initialization
    string firstName = "Jane";
    string lastName = "Doe";
    
    // 2. Concatenation (joining strings) using the '+' operator
    string fullName = firstName + " " + lastName;
    
    cout << "Full Name: " << fullName << endl;
    
    // 3. User Input (using getline for full sentences/names with spaces)
    string userHobby;
    cout << "Enter your favorite hobby: ";
    // getline is used to read an entire line, including spaces.
    getline(cin, userHobby); 
    
    // 4. Using the built-in 'length()' member function
    cout << "Your hobby is " << userHobby << endl;
    cout << "The length of your hobby string is: " << userHobby.length() << " characters." << endl;
    
    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Arrays",
      "Strings",
      "Multi-dimensional",
      "C-style strings",
      "std::string",
    ],
    date: "2024-10-07",
  },
  {
    id: 1008,
    topic: "c++",
    seriesKey: "c++",
    title: "Pointers and References in C++",
    description:
      "Understanding memory addresses, pointers, references, and their applications.",
    content: `
##  Pointers in C++
Pointers are variables that store memory addresses of other variables.
### Syntax and Declaration
**1.Declaration **
To declare a pointer, you use the dereference operator (*) between the data type and the pointer name. The pointer must be declared to point to a specific type of data.
\`\`\`cpp
dataType* pointerName;
\`\`\`
** Example: **
\`\`\`cpp
int* ip;       // Pointer to an integer (ip is pronounced "i-pointer")
double* dp;    // Pointer to a double
char* cp;      // Pointer to a character
\`\`\`
** 2.2. Assignment and Initialization **
You assign a pointer the address of a variable using the address-of operator (&).
\`\`\`cpp
int var = 10;   // Declare an integer variable
int* ptr;       // Declare an integer pointer

ptr = &var;     // Assign the address of 'var' to 'ptr'
\`\`\`
** 3.Dereferencing (Accessing Value)**
To get the value stored at the address a pointer holds, you dereference it using the * operator.
\`\`\`cpp
// Assuming 'ptr' holds the address of 'var'
int value = *ptr; // 'value' now equals 10
\`\`\`
### Simple Program Example
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    int number = 42;
    int *ptr;  // Pointer declaration
    
    ptr = &number;  // Store address of number in pointer
    
    cout << "Value of number: " << number << endl;
    cout << "Address of number: " << &number << endl;
    cout << "Value of pointer (address stored): " << ptr << endl;
    cout << "Value at pointer address (dereferencing): " << *ptr << endl;
    
    // Modifying value through pointer
    *ptr = 100;
    cout << "After modification through pointer:" << endl;
    cout << "Value of number: " << number << endl;
    cout << "Value at pointer address: " << *ptr << endl;
    
    return 0;
}
\`\`\`
### Types and Related Concepts
Pointers aren't categorized into strictly separate types like functions, but rather by their usage, behavior, and how they relate to other C++ features.
**1.Generic Pointer (void)** 
A void pointer (or generic pointer) can hold the address of any data type.
\`\`\`cpp
int num = 50;
void* vPtr = &num; // vPtr holds the address of an integer
// cout << *vPtr; // ERROR: Cannot dereference void*
cout << *static_cast<int*>(vPtr); // OK: Cast and dereference
\`\`\`
**2.Null Pointer **
A pointer that doesn't point to any memory location is a null pointer.
\`\`\`cpp
int* myPtr = nullptr; // Modern C++ standard
if (myPtr == nullptr) {
    // Check if the pointer is valid before using it
}
\`\`\`
** 3.Pointers and Arrays **
An array name, when used without an index, acts as a constant pointer to the array's first element.
\`\`\`cpp
int arr[] = {1, 2, 3};
int* aPtr = arr;    // aPtr now points to arr[0] (address of 1)
cout << *aPtr;      // Output: 1
cout << *(aPtr + 1); // Output: 2 (Pointer Arithmetic)
\`\`\`
** 4.Function Pointers **
A pointer that stores the starting address of a function. This allows you to pass functions as arguments to other functions or store them in data structures.
\`\`\`cpp
// Declares a pointer 'funcPtr' to a function that takes two ints and returns an int
int (*funcPtr)(int, int);
\`\`\`
** 5.Dynamic Memory Pointers **
Pointers are essential for Dynamic Memory Allocation using the new and delete operators. These pointers point to memory allocated from the heap (free store), which persists until explicitly deallocated.
\`\`\`cpp
// Allocate memory for a single integer on the heap
int* dynamicInt = new int; 
*dynamicInt = 55;

// Deallocate the memory to prevent a memory leak
delete dynamicInt; 
dynamicInt = nullptr; // Set to nullptr after deletion (best practice)
\`\`\`
##  References in C++
References are aliases for existing variables or A reference is essentially a second name for a variable that has already been declared
### Key Characteristics:
** Initialization: ** A reference must be initialized at the time of declaration. It cannot be left uninitialized.
** No Reassignment : **Once a reference is initialized to an object, it cannot be changed to refer to another object. It permanently binds to the first object.
** Not a Pointer : ** A reference is not a pointer. It doesn't use the dereference operator for access, and you cannot perform arithmetic operations on it. It simply acts as the original variable.
** No Null Reference: ** Unlike pointers, a reference cannot be NULL or refer to no object.
### Syntax and Declaration
The syntax for declaring a reference uses the address-of operator (&) after the data type.
\`\`\`cpp
dataType& referenceName = existingVariable;
\`\`\`
### Example: Reference Basics
\`\`\`cpp
#include <iostream>
using namespace std;

int main() {
    int original = 50;
    int &ref = original;  // Reference declaration
    
    cout << "Original value: " << original << endl;
    cout << "Reference value: " << ref << endl;
    
    // Modifying through reference
    ref = 100;
    cout << "After modifying through reference:" << endl;
    cout << "Original value: " << original << endl;
    cout << "Reference value: " << ref << endl;
    
    // References in function parameters (pass by reference)
    auto swap = [](int &a, int &b) {
        int temp = a;
        a = b;
        b = temp;
    };
    
    int x = 5, y = 10;
    cout << "Before swap: x = " << x << ", y = " << y << endl;
    swap(x, y);
    cout << "After swap: x = " << x << ", y = " << y << endl;
    
    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Pointers",
      "References",
      "Memory Address",
      "Dereferencing",
      "Pointer Arithmetic",
    ],
    date: "2024-10-08",
  },
  {
    id: 1009,
    topic: "c++",
    seriesKey: "c++",
    title: "Structures, Unions, and Dynamic Memory",
    description:
      "Working with user-defined types, unions, and dynamic memory allocation.",
    content: `
##  Structures in C++
A structure in C++ is a user-defined composite data type that allows you to bundle data items of different data types together.
**Syntax**
\`\`\`cpp
struct StructureName {
    dataType member1;
    dataType member2;
    // ...
};

// Declaration of struct variables (objects)
StructureName variable1;
StructureName variable2;
\`\`\`
** Example: Storing Book Information **
This example shows how to define a Book structure and access its members using the dot operator (.).
\`\`\`cpp
#include <iostream>
#include <string>
using namespace std;

// 1. Structure Definition
struct Book {
    string title;
    string author;
    int publicationYear;
    float price;
};

int main() {
    // 2. Declaration and Initialization of a struct variable
    Book myBook = {"The C++ Programming Language", "Bjarne Stroustrup", 2013, 75.50};

    // 3. Accessing members using the dot operator (.)
    cout << "--- Book Details ---" << endl;
    cout << "Title: " << myBook.title << endl;
    cout << "Author: " << myBook.author << endl;
    
    // 4. Modifying a member
    myBook.price = 80.00;
    cout << "Price (Updated): $" << myBook.price << endl;

    return 0;
}
\`\`\`
## Unions in C++
A union is a special data type that allows different members to occupy the same memory location. The memory allocated to a union is just large enough to hold the largest member of the union.
** Syntax **
\`\`\`cpp
union UnionName {
    dataType member1; // Usually the smallest
    dataType member2; // Usually the largest (determines union size)
    // ...
};

// Declaration of union variables (objects)
UnionName variable1;
UnionName variable2;
\`\`\`
** Example : Storing a Data ID **
This example defines a union that can store a small integer, a larger long integer, or a character array, but not all at once.
\`\`\`cpp
#include <iostream>
#include <cstring> // For strncpy
using namespace std;

// 1. Union Definition
union DataID {
    int shortCode;        // 4 bytes (on most systems)
    long long serialNum;  // 8 bytes (largest member, determines union size)
    char uniqueName[4];   // 4 bytes
}; 

int main() {
    DataID myID;

    // The size of the union is 8 bytes (the size of the largest member: long long)
    cout << "Size of DataID union: " << sizeof(myID) << " bytes" << endl; 

    // 2. Setting the first member (writes 4 bytes to the start of the 8-byte memory block)
    myID.shortCode = 101;
    cout << "Short Code: " << myID.shortCode << endl; // Valid reading

    // 3. Setting the second member (overwrites the entire 8-byte memory block)
    myID.serialNum = 9876543210LL;
    cout << "Serial Number: " << myID.serialNum << endl; // Valid reading
    
    // The previous value (shortCode) is now corrupted or overwritten.
    cout << "Short Code (Corrupted): " << myID.shortCode << endl; 

    // 4. Setting the third member (writes 4 bytes to the start of the 8-byte memory block)
    strncpy(myID.uniqueName, "ID01", 4);
    
    cout << "Unique Name: ";
    for(int i = 0; i < 4; ++i) {
        cout << myID.uniqueName[i];
    }
    cout << endl; // Valid reading
    
    return 0;
}
\`\`\`
##  Dynamic Memory Allocation
Dynamic memory allocation in C++ is the process of allocating memory manually during program execution (runtime) from the heap (or "free store"), rather than relying on automatic memory allocation on the stack.
### Syntax and Operators 
C++ uses two special operators for managing dynamic memory:
- new : Allocates memory from the heap and returns the starting address of the block.
- delete : Releases the memory pointed to by a pointer back to the heap. Crucial for preventing memory leaks.
** 1. Allocating Single Variables**
- Syntax 
\`\`\`cpp
dataType* pointerName = new dataType;
\`\`\`
- Example
\`\`\`cpp
int* iPtr = new int;      // Allocates memory for one integer
*iPtr = 50;               // Assigns value 50 to the memory location
\`\`\`
** 2.Deallocating Single Variables**
- Syntax
\`\`\`cpp
delete pointerName;
\`\`\`
- Example
\`\`\`cpp
delete iPtr;
iPtr = nullptr; // Good practice: set pointer to nullptr after deletion
\`\`\`
** 3. Allocating Arrays**
- Syntax
\`\`\`cpp
dataType* pointerName = new dataType[size];
\`\`\`
- Example
\`\`\`cpp
int size = 10;
int* arrPtr = new int[size]; // Allocates memory for an array of 10 integers
\`\`\`
** 4. Deallocating Arrays**
- Syntax
\`\`\`cpp
delete[] pointerName; // The square brackets are mandatory for arrays!
\`\`\`
- Example
\`\`\`cpp
delete[] arrPtr;
arrPtr = nullptr;
\`\`\`

### Dynamic Memory Allocation Functions
These functions originated in the C language and are available in C++ via the **stdlib (or stdlib.h)** header. They work with raw, untyped memory.
**1.malloc() (Memory Allocation) **
malloc() allocates a block of memory of the specified number of bytes from the heap. It does not initialize the memory; the contents are initially garbage (indeterminate values).
- Syntax
\`\`\`cpp
void* malloc(size_t size);
\`\`\`
**Note **
- size_t size: The number of bytes to allocate.
- Return Value: Returns a void* pointer to the allocated memory block. This pointer must be explicitly cast to the required data type. Returns NULL if allocation fails.
**Example **
\`\`\`cpp
int* iPtr = (int*)malloc(sizeof(int)); // Allocate memory for one int
// Check if allocation failed
if (iPtr != nullptr) {
    *iPtr = 42;
}
\`\`\`
** 2.calloc() (Contiguous Allocation)**
calloc() allocates memory for an array of elements. Crucially, it initializes all allocated memory to zero.
- Syntax
\`\`\`cpp
void* calloc(size_t num, size_t size);
\`\`\`
**Note**
- size_t num: The number of elements to allocate.
- size_t size: The size of each element in bytes.
- Return Value: Returns a void* pointer to the allocated, zero-initialized memory. Returns NULL on failure.
**Example **
\`\`\`cpp
// Allocate memory for 5 integers and initialize all to 0
int* arrPtr = (int*)calloc(5, sizeof(int));
// The memory at arrPtr[0] through arrPtr[4] is guaranteed to be 0.
\`\`\`
** 3.free() (Deallocation)**
free() deallocates the memory block pointed to by the given pointer, returning it to the heap. It must be used to release memory allocated by malloc, calloc, or realloc.
- Syntax 
\`\`\`cpp
void free(void* ptr);
//void* ptr: The pointer returned by malloc, calloc, or realloc.
\`\`\`
- Example
\`\`\`cpp
// Releasing memory allocated by malloc/calloc
free(iPtr);
free(arrPtr); 
// Pointers should then be set to nullptr
iPtr = nullptr;
arrPtr = nullptr;
\`\`\`
`,
    tags: [
      "C++",
      "Structures",
      "Unions",
      "Dynamic Memory",
      "new",
      "delete",
      "Memory Management",
    ],
    date: "2024-10-09",
  },
  {
    id: 1010,
    topic: "c++",
    seriesKey: "c++",
    title: "OOPs Fundamentals",
    description:
      "Introduction to Object-Oriented Programming with classes, objects, and access control.",
    content: `
##  Class in C++
A Class is a user-defined blueprint or a template from which objects are created. It serves as a plan for a set of objects that share common attributes (data) and behavior (methods or functions).
** Syntax **
\`\`\`cpp
class ClassName {
    // Access Specifier (e.g., public, private)
private:
    // Data Members (Attributes/Properties)
    dataType memberVariable1;

public:
    // Member Functions (Methods/Behaviors)
    void memberFunction1() {
        // ... code ...
    }
};
\`\`\`
** Example : Defining a Laptop Class
\`\`\`cpp
class Laptop {
private:
    // 1. Data Members (Attributes): These hold the object's state
    std::string model;
    int ramGB;
    double price;

public:
    // 2. Member Function (Constructor): Used to initialize the object
    Laptop(std::string m, int ram, double p) {
        model = m;
        ramGB = ram;
        price = p;
    }

    // 3. Member Function (Method): Defines the object's behavior
    void displaySpecs() const {
        std::cout << "Model: " << model 
                  << " | RAM: " << ramGB << "GB" 
                  << " | Price: $" << price << std::endl;
    }
};
\`\`\`
### Object 
An Object is an instance of a class. It is a real-world entity that has a state (data stored in attributes) and a behavior (actions defined by methods).
** Syntax **
An object is declared like a regular variable, using the class name as the data type:
\`\`\`cpp
ClassName objectName;
// OR (using a constructor):
ClassName objectName(arguments);
\`\`\`
** Example: Creating and Using Laptop Objects **
\`\`\`cpp
#include <iostream>
#include <string>
using namespace std;

// (The Laptop class defined above is assumed here)

class Laptop {
private:
    string model;
    int ramGB;
    double price;

public:
    // Constructor
    Laptop(string m, int ram, double p) : model(m), ramGB(ram), price(p) {}

    // Method
    void displaySpecs() const {
        cout << "Model: " << model 
             << " | RAM: " << ramGB << "GB" 
             << " | Price: $" << price << endl;
    }
};

int main() {
    // 1. Creating Objects (Instances) of the Laptop class
    Laptop laptop1("Dell XPS", 16, 1200.00); // Object 1
    Laptop laptop2("MacBook Air", 8, 999.00); // Object 2
    
    // Each object has its own unique set of data members (state).
    
    // 2. Calling Methods (Behaviors) using the objects
    cout << "--- Laptop 1 Specs ---" << endl;
    laptop1.displaySpecs(); 

    cout << "--- Laptop 2 Specs ---" << endl;
    laptop2.displaySpecs();
    
    // Object 1 and Object 2 are separate instances in memory.
    
    return 0;
}
\`\`\`
### Object-Oriented Programming (OOP)
Object-Oriented Programming (OOP) is a programming paradigm based on the concept of "objects," which can contain both data (attributes) and code (methods/functions). 
The four foundational pillars of OOP are Encapsulation, Abstraction, Inheritance, and Polymorphism.
### 1.Encapsulation  
Encapsulation is the mechanism of bundling data (variables) and the methods (functions) that operate on that data into a single unit (a class). It also involves data hiding, restricting direct access to some of an object's components, which is typically achieved using access specifiers.
** Syntax (Access Specifiers) **
C++ provides three access specifiers to enforce encapsulation:
** 1.public: ** Members are accessible from anywhere.
** 2.protected: ** Members are accessible within the class and by derived classes (inheritance).
** 3. private: ** Members are accessible only from within the class itself.
** Example: Encapsulation **
\`\`\`cpp
#include <iostream>
#include <string>
using namespace std;

class Account {
private:
    double balance; // Private data member (hidden)
    
public:
    // Constructor to initialize the object
    Account(double initialBalance) {
        // Public method to set initial balance safely
        if (initialBalance >= 0) {
            balance = initialBalance;
        } else {
            balance = 0;
            cout << "Initial balance must be non-negative." << endl;
        }
    }

    // Public Method: The only way to deposit (controlled access)
    void deposit(double amount) {
        if (amount > 0) {
            balance += amount;
        }
    }

    // Public Method: The only way to retrieve the balance
    double getBalance() const {
        return balance;
    }
    
    // The data is "encapsulated" within the class.
};

int main() {
    Account myAccount(100.0);
    
    // myAccount.balance = -500; // ERROR: Cannot access private member directly
    
    myAccount.deposit(50.0);
    
    cout << "Current Balance: $" << myAccount.getBalance() << endl; 
    
    return 0;
}
\`\`\`
### Abstraction 
Abstraction is the concept of displaying only essential information to the user while hiding complex implementation details. It focuses on what an object does, rather than how it achieves it.
** Implementation  ** 
Abstraction is achieved in C++ primarily through:
- Classes: (Using public methods to hide private data, as shown in the encapsulation example).
- Abstract Classes / Interfaces (via Pure Virtual Functions): A class containing at least one pure virtual function (= 0) cannot be instantiated and serves as a blueprint for derived classes.
**Example: Abstraction (via Class Interface) ** 
The Account class in the Encapsulation example also demonstrates abstraction. The user interacts only with deposit() and getBalance(), without needing to know how the balance is stored or updated internally.
\`\`\`cpp
// (Using the same Account class from above)

// User Code (Abstracted View):
// The user only sees the function names and their effects, not the internal 'balance' variable.
/*
    Account myAccount(100.0); 
    myAccount.deposit(50.0);  // The 'how' is hidden.
    double cash = myAccount.getBalance(); // The 'how' is hidden.
*/
\`\`\`
### Inheritance 
Inheritance is the mechanism where a new class (derived class or subclass) is created from an existing class (base class or superclass). The derived class inherits the properties (members) and behavior (methods) of the base class.
** Syntax **
\`\`\`cpp
// Base Class Definition (the class being inherited from)
class BaseClass {
    // ... members ...
};

// Derived Class Definition
class DerivedClass : access_specifier BaseClass {
    // ... new members ...
};
\`\`\`
** Example: Inheritance ** 
\`\`\`cpp
#include <iostream>
using namespace std;

// Base Class
class Animal {
public:
    void eat() const {
        cout << "The animal is eating." << endl;
    }
    void sleep() const {
        cout << "The animal is sleeping." << endl;
    }
};

// Derived Class: Dog inherits publicly from Animal
// A Dog "is-a" Animal, so it gets 'eat()' and 'sleep()' for free.
class Dog : public Animal {
public:
    void bark() const {
        cout << "Woof! Woof!" << endl;
    }
    // The Dog also implicitly has the eat() and sleep() methods.
};

int main() {
    Dog myDog;
    
    // Methods inherited from the Animal base class
    myDog.eat(); 
    myDog.sleep();
    
    // Method unique to the Dog derived class
    myDog.bark(); 
    
    return 0;
}
\`\`\`
### Polymorphism 
Polymorphism (meaning "many forms") is the ability of an object or function to take on multiple forms. It allows one interface to be used for a general class of actions.
### Types of Polymorphism 
**1. Compile-time Polymorphism (Static Binding):** Resolved during compilation.
- Function Overloading: Multiple functions with the same name but different parameters (number or type).
- Operator Overloading: Operators (+, -, *, etc.) redefined to work with user-defined types (like classes).
**2. Runtime Polymorphism (Dynamic Binding): Resolved during program execution. **
- Achieved using Virtual Functions and Base Class Pointers/References (as shown in the earlier detailed example).
**Example: Compile-time Polymorphism (Function Overloading)**
\`\`\`cpp
#include <iostream>
#include <string>
using namespace std;

class Calculator {
public:
    // 1. Function Overloading: add for integers
    int add(int a, int b) {
        return a + b;
    }

    // 2. Function Overloading: add for doubles (different parameter types)
    double add(double a, double b) {
        return a + b;
    }
    
    // 3. Function Overloading: add for three integers (different number of parameters)
    int add(int a, int b, int c) {
        return a + b + c;
    }
};

int main() {
    Calculator calc;
    
    // The compiler determines which 'add' function to call based on the arguments' types and number.
    cout << "Sum of integers (5 + 3): " << calc.add(5, 3) << endl; 
    cout << "Sum of doubles (5.5 + 3.2): " << calc.add(5.5, 3.2) << endl; 
    cout << "Sum of three integers (1 + 2 + 3): " << calc.add(1, 2, 3) << endl; 
    
    return 0;
}
\`\`\`

`,
    tags: [
      "C++",
      "OOP",
      "Classes",
      "Objects",
      "Constructors",
      "Destructors",
      "Access Specifiers",
      "Static Members",
    ],
    date: "2024-10-10",
  },
  {
    id: 1011,
    topic: "c++",
    seriesKey: "c++",
    title: "Constructors & Destructors",
    description:
      "Understanding different types of constructors and destructors for object initialization and cleanup.",
    content: `
 ### Constructors and Destructors
Constructors and Destructors are essential member functions in C++ classes that manage the lifecycle of an object.
### Constructors 
A Constructor is a special member function of a class that is automatically called when an object of that class is created (instantiated). Its primary purpose is to initialize the object's data members.
### Key Characteristics
- Name: Must have the exact same name as the class.
- Return Type: Has no return type (not even void).
- Purpose: To set up initial values for the object's state.
** Syntax **
\`\`\`cpp
class ClassName {
public:
    // Syntax for a simple constructor
    ClassName() { 
        // Initialization code here
    }
    
    // Syntax for a parameterized constructor
    ClassName(dataType param1, dataType param2) {
        // Initialization using parameters
    }
};
\`\`\`
### Types of Constructors
** 1. Default Constructor: ** A constructor that takes no arguments. If you don't define any constructor, the compiler provides a public, inline default constructor.
** 2. Parameterized Constructor: ** A constructor that takes one or more arguments to initialize the data members with user-supplied values.
** 3. Copy Constructor: ** A special constructor that takes a reference to an object of the same class as an argument. It's used to create a new object as a copy of an existing object.
** 4. Move Constructor (C++11):** Used to create an object by "moving" resources (like dynamically allocated memory) from an existing temporary object, improving efficiency.
### Destructors 
A Destructor is a special member function that is automatically called when an object is about to be destroyed (goes out of scope, is explicitly deleted, etc.).
### Key Characteristics
- Name: Must have the exact same name as the class, preceded by a tilde (~).
- Return Type: Has no return type (not even void).
- Arguments: Cannot take any arguments.
- Purpose: To perform cleanup activities, such as releasing dynamically allocated memory or closing file connections.
** Syntax **
\`\`\`cpp
class ClassName {
public:
    // Syntax for the destructor
    ~ClassName() { 
        // Cleanup code (e.g., delete[] pointers)
    }
};
\`\`\`
### Simple Program Example (Constructor & Destructor)
\`\`\`cpp
#include <iostream>
using namespace std;

class Resource {
private:
    int* data;         // Pointer to dynamically allocated memory
    string name;       // Simple data member

public:
    // 1. Parameterized Constructor
    Resource(string n, int value) {
        name = n;
        data = new int;    // Allocate memory on the heap
        *data = value;
        cout << "Constructor called for " << name << ": Allocated memory and set value to " << *data << endl;
    }
    
    // 2. Default Constructor
    Resource() {
        name = "Default";
        data = new int;
        *data = 0;
        cout << "Default Constructor called. (Value set to 0)" << endl;
    }

    // Accessor method
    void display() const {
        cout << name << " Data Value: " << *data << endl;
    }

    // 3. Destructor
    // Called when the object 'r1' or 'r2' goes out of scope.
    ~Resource() {
        // Essential cleanup: release the memory allocated by 'new'
        delete data; 
        cout << "Destructor called for " << name << ": Memory deallocated." << endl;
        // The object is now safe to be destroyed.
    }
};

int main() {
    // A block to show scope-based destruction
    { 
        // Calls Parameterized Constructor
        Resource r1("Primary Resource", 100); 
        r1.display();
        
        // Calls Default Constructor
        Resource r2; 
        r2.display();

    } // r2 goes out of scope first, then r1. Destructors are called automatically here.
    
    cout << "\nProgram execution continues after objects are destroyed." << endl;
    
    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Constructors",
      "Destructors",
      "Default Constructor",
      "Copy Constructor",
      "Initialization List",
      "Object Lifecycle",
    ],
    date: "2024-10-11",
  },
  {
    id: 1012,
    topic: "c++",
    seriesKey: "c++",
    title: "Inheritance in C++",
    description:
      "Implementing inheritance to create class hierarchies and reuse code efficiently.",
    content: `
##  Inheritance in C++
Inheritance is a mechanism in which one class (derived class or child class) acquires the members (data and methods) of another class (base class or parent class).

** Syntax **
The derived class is declared using a colon (:) followed by the access specifier and the base class name.
\`\`\`cpp
class BaseClass {
    // ... members ...
};

class DerivedClass : access_specifier BaseClass {
    // ... new members and methods ...
};
\`\`\`

### Types of Inheritance 
C++ supports five main types of inheritance, based on the structure of the relationship between classes.
** 1.Single Inheritance: ** A derived class inherits from only one base class. 
                           ** Base ➡️ Derived **
 - Syntax 
 \`\`\`cpp  
class Derived : public Base {
    // Derived class members
};
 \`\`\`
 ** 2. Multilevel Inheritance: ** A class inherits from a base class, which itself is a derived class.
 ** Base ➡️ Intermediate ➡️ Derived **
 - Syntax
 \`\`\`cpp
// First Level of Inheritance
class Intermediate : public Base {
    // Intermediate class members
};

// Second Level of Inheritance
class Derived : public Intermediate {
    // Derived class members
};
 \`\`\`
 ** 3. Hierarchical Inheritance **
 Multiple derived classes inherit from a single base class.
 ** Base ➡️ Derived 1,Base ➡️ Derived 2**
 - Syntax
 \`\`\`cpp
// Derived Class 1
class Derived1 : public Base {
    // Derived1 members
};

// Derived Class 2
class Derived2 : public Base {
    // Derived2 members
};
 \`\`\`
 **4. Multiple Inheritance **
  A single derived class inherits from two or more independent base classes.
** BaseA,BaseB ➡️ Derived **
  - Syntax
  \`\`\`cpp
class Derived : public BaseA, public BaseB {
    // Derived class members inheriting from both BaseA and BaseB
};
  \`\`\`
  **5. Hybrid Inheritance **
  Hybrid inheritance is a structural pattern and does not have a unique, single syntax. It is achieved by combining the syntax of two or more other types.
 ** Base ➡️ DerivedA,Base ➡️ DerivedB,DerivedA,DerivedB ➡️ FinalDerived **
- Syntax
\`\`\`cpp
// Hierarchical Part
class DerivedA : public Base {};
class DerivedB : public Base {};

// Multiple Part (Creates the Hybrid structure)
class FinalDerived : public DerivedA, public DerivedB {
    // FinalDerived members
};
\`\`\`

** Note : **
- The public access specifier in the syntax examples can be replaced with protected or private depending on the required accessibility within the derived class.

### Inheritance Access Modes
The access_specifier used during inheritance (public, protected, or private) determines the accessibility of the base class's members within the derived class.
- Rule: Private members of the base class are never directly accessible in the derived class, regardless of the inheritance mode.
### Simple Program Example (Hybrid Inheritance)
This example combines Hierarchical and Multiple inheritance to demonstrate a Hybrid structure.
\`\`\`cpp
#include <iostream>
#include <string>
using namespace std;

// Base Class 1 (Parent for Hierarchical structure)
class Appliance {
protected:
    string brand;
public:
    Appliance(string b) : brand(b) {}
    void showBrand() const {
        cout << "Brand: " << brand;
    }
};

// Base Class 2 (One of the parents for Multiple structure)
class Device {
private:
    int powerWatts; // Private members are NOT accessible to derived classes
protected: 
    bool hasRemote; // Accessible to derived classes
public:
    Device(int w, bool r) : powerWatts(w), hasRemote(r) {}
    void checkRemote() const {
        cout << (hasRemote ? " (Has Remote)" : " (No Remote)");
    }
};

// --- Hierarchical Inheritance ---
// Derived Class A: Tablet (inherits from Device)
class Tablet : public Device {
public:
    Tablet(int w, bool r) : Device(w, r) {}
    void isPortable() const {
        cout << "Tablet is Portable." << endl;
    }
};

// --- Multiple and Hybrid Inheritance ---
// Derived Class B: SmartTV (inherits from Appliance AND Device)
class SmartTV : public Appliance, public Device {
private:
    int screenSize;
public:
    // Constructor initializes both base classes
    SmartTV(string b, int w, bool r, int s)
        : Appliance(b), Device(w, r), screenSize(s) {}

    // Method to display all inherited and local properties
    void displayInfo() const {
        cout << "--- SmartTV Details ---" << endl;
        // Accessing Base 1 public method
        showBrand(); 
        // Accessing Base 2 public method
        checkRemote(); 
        cout << endl;
        
        // Accessing local member
        cout << "Screen Size: " << screenSize << " inches" << endl;
        
        // Accessing protected member 'hasRemote' directly from Base 2
        if (hasRemote) { 
            cout << "Feature: Network Ready." << endl;
        }
    }
};

int main() {
    // 1. Example of Single/Hierarchical Use
    Tablet myTablet(15, true);
    myTablet.isPortable();

    // 2. Example of Multiple/Hybrid Use
    SmartTV lgTV("LG", 85, true, 55);
    lgTV.displayInfo();

    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Inheritance",
      "Base Class",
      "Derived Class",
      "Multiple Inheritance",
      "Access Specifiers",
      "Virtual Functions",
    ],
    date: "2024-10-12",
  },
  {
    id: 1013,
    topic: "c++",
    seriesKey: "c++",
    title: "Polymorphism",
    description:
      "Implementing polymorphism through function overloading, operator overloading, and virtual functions.",
    content: `
##  Polymorphism in C++
Polymorphism is the ability of an object or function to take on multiple forms. In C++, it allows you to define one function name or operator symbol that behaves differently depending on the context

### Types of Polymorphism
C++ implements polymorphism through two primary mechanisms:
**1.Compile-time Polymorphism (Static Binding)**
This type of polymorphism is resolved by the compiler during the program's compilation. The compiler knows exactly which function or operation to execute based on the function signature (name and parameters).
It is of two types : 
** a. Function Overloading: ** 
Defining multiple functions with the same name but different parameter lists (different number or types of arguments).
- Mechanism: Same function name, different parameter list.
** b. Operator Overloading: **
Redefining how a standard operator (like +, -, or *) works when applied to user-defined types (objects).
- Mechanism: Using the operator keyword. 
** 2.Run-Time Polymorphism (Dynamic Binding) **
This type of polymorphism is resolved during the program's execution (runtime). It is the mechanism that allows a base class pointer or reference to call the correct derived class function implementation.
**a. Virtual Function **
Achieved by declaring a member function in the base class with the virtual keyword and overriding it in the derived classes.
- Mechanism: Use of the virtual keyword and a Base Class Pointer/Reference.

### Example Program (Function Overloading & Virtual Functions)
This program demonstrates both static (function overloading) and dynamic (virtual functions) polymorphism.
\`\`\`cpp
#include <iostream>
#include <string>
using namespace std;

// --- 1. Compile-Time Polymorphism (Function Overloading) ---
class Calculator {
public:
    // Overloaded function 1: Adds two integers
    int add(int a, int b) {
        cout << "Calling int adder: ";
        return a + b;
    }

    // Overloaded function 2: Adds two doubles (same name, different parameter types)
    double add(double a, double b) {
        cout << "Calling double adder: ";
        return a + b;
    }
};

// --- 2. Run-Time Polymorphism (Virtual Functions) ---

// Base Class
class Shape {
public:
    // Virtual Function: The 'virtual' keyword enables runtime polymorphism.
    // The '= 0' makes this a Pure Virtual Function, making Shape an Abstract Class.
    virtual void draw() const = 0; 
    
    // Virtual Destructor: Essential for base classes to ensure derived objects are fully cleaned up.
    virtual ~Shape() = default; 
};

// Derived Class 1
class Circle : public Shape {
public:
    // Override the virtual function
    void draw() const override {
        cout << "Drawing a Circle." << endl;
    }
};

// Derived Class 2
class Square : public Shape {
public:
    void draw() const override {
        cout << "Drawing a Square." << endl;
    }
};

int main() {
    // --- Compile-Time Example ---
    cout << "--- Static Polymorphism (Overloading) ---" << endl;
    Calculator calc;
    
    // The compiler knows at compile time to call the int version
    cout << calc.add(10, 5) << endl;
    
    // The compiler knows at compile time to call the double version
    cout << calc.add(3.5, 2.1) << endl;
    
    cout << "\n--- Dynamic Polymorphism (Virtual Functions) ---" << endl;

    // 1. Create an array of Base Class Pointers. This is the key to dynamic binding.
    Shape* shapes[2]; 

    // 2. Point Base Pointers to Derived Objects
    shapes[0] = new Circle();
    shapes[1] = new Square();

    // 3. Loop and call the method
    for (int i = 0; i < 2; ++i) {
        // At runtime, the program checks the actual object type (Circle or Square)
        // that the pointer is holding and calls the correct 'draw()' method.
        shapes[i]->draw(); 
    }

    // 4. Cleanup (using the virtual destructor)
    delete shapes[0];
    delete shapes[1];

    return 0;
}
\`\`\`


### Abstract Classes and Interfaces
Abstract classes and interfaces are key concepts in C++ used for achieving abstraction and runtime polymorphism.
### 1. Abstract Classes 
An Abstract Class is a class designed to be used as a base class. It cannot be instantiated (you cannot create an object directly from it) because it is incomplete; it contains one or more pure virtual functions.
### Key Characteristics
- Instantiability: You cannot create objects of an abstract class.
- Purpose: To define a common interface and enforce that derived classes provide specific implementations for certain functions.
- Content: Can have data members, normal member functions, constructors, and destructors, in addition to pure virtual functions.
** Pure Virtual Function **
A pure virtual function is a virtual function declared in the base class that has no definition. Its purpose is to force derived classes to implement that function.
** Syntax **
\`\`\`cpp
virtual returnType functionName(parameters) = 0;
\`\`\`
**Example **
\`\`\`cpp
#include <iostream>
#include <string>
using namespace std;

// Abstract Class: Cannot be instantiated due to the pure virtual function.
class Vehicle {
protected:
    string name;
public:
    Vehicle(string n) : name(n) {}

    // Pure Virtual Function: Forces all derived classes to implement startEngine().
    virtual void startEngine() const = 0; 
    
    // Regular virtual function (optional to override)
    virtual void showInfo() const {
        cout << "Vehicle: " << name;
    }

    // Must have a virtual destructor in a polymorphic base class
    virtual ~Vehicle() = default; 
};

// Concrete Derived Class
class Car : public Vehicle {
public:
    Car(string n) : Vehicle(n) {}

    // Must implement the pure virtual function (startEngine)
    void startEngine() const override {
        cout << name << " started with a key ignition." << endl;
    }

    void showInfo() const override {
        cout << "Car: " << name << " is ready." << endl;
    }
};

int main() {
    // Vehicle v; // ERROR: Cannot declare variable of abstract type 'Vehicle'

    Vehicle* myVehicle = new Car("Toyota"); // OK: Pointer to base points to derived object
    
    myVehicle->showInfo();
    myVehicle->startEngine();

    delete myVehicle;
    return 0;
}
\`\`\`
### Interfaces (via Abstract Classes) 
C++ does not have a built-in keyword for an "interface" like Java or C#. Instead, an Interface in C++ is achieved by defining a class that contains only pure virtual functions.
### Key Characteristics
- Instantiability: Cannot be instantiated (it's an abstract class).
- Purpose: To establish a contract. Any class inheriting from this "interface" must implement every function defined in the base class.
- Content: Contains only public pure virtual functions and perhaps a virtual destructor. It typically has no data members.
** Syntax **
\`\`\`cpp
#include <iostream>
using namespace std;

// Interface: All methods are pure virtual.
class ILoggable {
public:
    // Pure virtual function
    virtual void log(const string& message) const = 0;
    
    // Pure virtual function
    virtual void timestamp() const = 0;
    
    // Required virtual destructor
    virtual ~ILoggable() = default; 
};

// Concrete Class: Must fulfill the contract defined by ILoggable.
class DatabaseConnector : public ILoggable {
public:
    // Must implement log()
    void log(const string& message) const override {
        cout << "[DB] Logging: " << message << endl;
    }

    // Must implement timestamp()
    void timestamp() const override {
        cout << "[DB] Time: 2024-10-20" << endl;
    }
};

int main() {
    // ILoggable logger; // ERROR

    ILoggable* loggerPtr = new DatabaseConnector();
    
    loggerPtr->timestamp();
    loggerPtr->log("Connection established successfully.");

    delete loggerPtr;
    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Polymorphism",
      "Virtual Functions",
      "Abstract Classes",
      "Function Overloading",
      "Operator Overloading",
      "Runtime Polymorphism",
      "Compile-time Polymorphism",
    ],
    date: "2024-10-13",
  },
  {
    id: 1014,
    topic: "c++",
    seriesKey: "c++",
    title: "Encapsulation and Abstraction",
    description:
      "Implementing data hiding through encapsulation and creating abstract interfaces.",
    content: `
##  Encapsulation and Abstraction
Encapsulation bundles data and methods, while abstraction hides complex implementation details.

### Encapsulation 
Encapsulation is the mechanism of bundling data (attributes) and the methods (functions) that operate on that data into a single unit (a class).
- Goal: To achieve data security and integrity by controlling access to the data.
## Mechanism and Implementation
In C++, encapsulation is enforced using access specifiers:
- private:Data members are typically declared as private. They can only be accessed or modified through the class's own methods.
- public:Member functions (often called getters and setters) are declared as public to provide the controlled interface for interacting with the private data.
** Example: Encapsulation **
\`\`\`cpp
#include <iostream>
using namespace std;

class Temperature {
private:
    double celsius; // Private: This is the hidden, encapsulated data.

public:
    // Public Setter (Controlled modification of data)
    void setCelsius(double temp) {
        if (temp >= -273.15) { // Validation logic inside the method
            celsius = temp;
        } else {
            cout << "Error: Temperature is below absolute zero!" << endl;
        }
    }

    // Public Getter (Controlled access to data)
    double getCelsius() const {
        return celsius;
    }

    // Public Method to perform an operation
    double toFahrenheit() const {
        // Internal logic is hidden
        return (celsius * 9.0 / 5.0) + 32.0;
    }
};

int main() {
    Temperature t;
    // t.celsius = 500.0; // ERROR: Direct access to private member is blocked (Encapsulation)

    t.setCelsius(25.0); // Access is controlled by the public interface
    
    cout << "Temp in Fahrenheit: " << t.toFahrenheit() << endl; 
    
    return 0;
}
\`\`\`
### Abstraction 
Abstraction is the concept of displaying only the essential information to the user while hiding the complex implementation details. It focuses on what an object does, rather than how it achieves it.
- Goal: To achieve simplicity and clarity by providing a clean, user-friendly interface.
### Mechanism and Implementation
Abstraction is achieved in C++ through:
- Classes/Encapsulation: By only exposing public methods (the interface) and hiding the private data and internal complexity.
- Abstract Classes and Interfaces: Using pure virtual functions (= 0) to define a contract that derived classes must follow, showing what functionality exists without providing the how.
** Example: Abstraction **
\`\`\`cpp
#include <iostream>
#include <string>

// --- Abstract Class: Vehicle ---
// This class serves as a blueprint (an abstract concept).
// It cannot be instantiated directly because it has a pure virtual function.
class Vehicle {
protected:
    std::string brand; // A data member that concrete vehicles will have.

public:
    // Constructor for the abstract class.
    // Derived classes will call this constructor.
    Vehicle(std::string b) : brand(b) {
        std::cout << "Vehicle created (Brand: " << brand << ")." << std::endl;
    }

    // --- Pure Virtual Function (Abstraction in action) ---
    // This is a contract! Any class that inherits from 'Vehicle' MUST implement
    // its own version of 'startEngine()'.
    // We are saying "Every Vehicle can start its engine," but we are not
    // specifying *how* it starts (e.g., key, button, crank).
    virtual void startEngine() const = 0; 

    // Regular virtual function (derived classes can override, but not required)
    virtual void displayInfo() const {
        std::cout << "This is a " << brand << " vehicle." << std::endl;
    }

    // IMPORTANT: A virtual destructor for a base class with virtual functions.
    // Ensures proper cleanup when deleting derived objects via base pointers.
    virtual ~Vehicle() {
        std::cout << "Vehicle destructor called for " << brand << "." << std::endl;
    }
};

// --- Concrete Class: Car ---
// 'Car' is a concrete implementation of a 'Vehicle'.
// It must provide the concrete details for 'startEngine()'.
class Car : public Vehicle {
private:
    int numDoors;
public:
    // Constructor calls the base class constructor
    Car(std::string b, int doors) : Vehicle(b), numDoors(doors) {
        std::cout << "Car object created." << std::endl;
    }

    // --- Implementing the Abstract Function ---
    // This is *how* a Car starts its engine.
    void startEngine() const override {
        std::cout << brand << " car starts with a turn of the key." << std::endl;
    }

    // Overriding a regular virtual function
    void displayInfo() const override {
        Vehicle::displayInfo(); // Call base class version first
        std::cout << "It has " << numDoors << " doors." << std::endl;
    }

    ~Car() {
        std::cout << "Car destructor called." << std::endl;
    }
};

// --- Concrete Class: ElectricScooter ---
// 'ElectricScooter' is another concrete implementation of a 'Vehicle'.
// It also provides its own specific 'startEngine()' method.
class ElectricScooter : public Vehicle {
public:
    // Constructor calls the base class constructor
    ElectricScooter(std::string b) : Vehicle(b) {
        std::cout << "Electric Scooter object created." << std::endl;
    }

    // --- Implementing the Abstract Function ---
    // This is *how* an Electric Scooter starts its engine.
    void startEngine() const override {
        std::cout << brand << " electric scooter starts with a power button press." << std::endl;
    }

    ~ElectricScooter() {
        std::cout << "Electric Scooter destructor called." << std::endl;
    }
};


int main() {
    std::cout << "--- Demonstrating Abstraction ---" << std::endl;

    // We cannot create an object of 'Vehicle' directly because it's abstract.
    // Vehicle myVehicle("Generic"); // ERROR! 'Vehicle' is abstract.

    // But we can use pointers to the abstract class type.
    // This allows us to treat different concrete vehicles generically.
    Vehicle* v1 = new Car("Ford", 4); 
    Vehicle* v2 = new ElectricScooter("Xiaomi");

    std::cout << "\n--- Operating Vehicles ---" << std::endl;

    // We call 'startEngine()' through the base class pointer.
    // The actual implementation (how it starts) is hidden from 'main()'.
    // 'main()' only knows that a 'Vehicle' can 'startEngine()'.
    v1->displayInfo();
    v1->startEngine(); // Calls Car's startEngine()

    std::cout << std::endl;

    v2->displayInfo();
    v2->startEngine(); // Calls ElectricScooter's startEngine()

    std::cout << "\n--- Cleaning up ---" << std::endl;

    // Using virtual destructors for proper cleanup
    delete v1;
    delete v2;

    std::cout << "\nProgram finished." << std::endl;

    return 0;
}
\`\`\`
### Getters (Accessors)
A Getter is a method used to retrieve (get) the value of a private member variable.
- Purpose: To allow external code to read the value of a hidden attribute without accessing the attribute directly.
- Syntax/Convention: Usually named starting with get (e.g., getName(), getBalance()). They are typically declared as const because they do not modify the object's state.
###Setters (Mutators)
A Setter is a method used to modify (set or change) the value of a private member variable.
- Purpose: To provide a single, controlled entry point for changing a private attribute. This allows the class to enforce validation or other business rules before the change is accepted.
- Syntax/Convention: Usually named starting with set (e.g., setName(string newName), setAge(int newAge)).
** Example **
\`\`\`cpp
#include <iostream>
#include <string>

class Employee {
private:
    std::string name;
    int age; // Private data member
    
public:
    // Constructor
    Employee(std::string n, int a) {
        // Use the setter for initial validation
        setName(n);
        setAge(a);
    }

    // --- Getter (Accessor) ---
    // Returns the value of the private 'age' member. Declared const.
    int getAge() const {
        return age;
    }

    // --- Setter (Mutator) ---
    // Sets the value of the private 'age' member. Includes validation.
    void setAge(int newAge) {
        // Validation Logic: Ensures age is realistic
        if (newAge >= 18 && newAge <= 65) {
            age = newAge;
            std::cout << "Age set successfully to " << newAge << "." << std::endl;
        } else {
            std::cout << "Error: Invalid age. Age remains unchanged." << std::endl;
        }
    }
    
    // Simple setter for string
    void setName(std::string newName) {
        name = newName;
    }

    // Simple getter for string
    std::string getName() const {
        return name;
    }
};

int main() {
    Employee emp("Alice", 30);
    
    // 1. Getting the value
    std::cout << "Initial Age: " << emp.getAge() << std::endl; // Access via getter
    
    // 2. Setting a valid value
    emp.setAge(45);
    
    // 3. Attempting to set an invalid value (validation blocks the change)
    emp.setAge(10); 
    
    std::cout << "Final Age: " << emp.getAge() << std::endl;
    
    return 0;
}
\`\`\`
### Abstraction with Abstract Classes
An Abstract Class is a fundamental concept in C++ for achieving abstraction and defining an interface that derived classes must adhere to.
** Abstract Class**
A class designed to be a base class that cannot be instantiated directly. It is incomplete because it contains one or more pure virtual functions.
** Pure Virtual Function **
A virtual function declared in the base class that has no definition (implementation). It acts as a contract, forcing all concrete derived classes to provide their own implementation for that function.
### Syntax and Mechanism
A class becomes abstract the moment you declare at least one pure virtual function within it.
\`\`\`cpp
class AbstractClassName {
public:
    // Pure Virtual Function: The "= 0" makes the class abstract.
    virtual returnType functionName(parameters) = 0; 

    // Regular functions, data members, and constructors are still allowed.
    // A virtual destructor is recommended for base classes.
    virtual ~AbstractClassName() = default; 
};
\`\`\`
### Simple Program Example
This example defines an abstract class Logger and ensures that any concrete logging implementation (like FileLogger or ConsoleLogger) follows the mandatory log() interface.
\`\`\`cpp
#include <iostream>
#include <string>
using namespace std;

// --- Abstract Class: Logger ---
// Defines the contract: Anything that inherits from Logger MUST implement log().
class Logger {
public:
    // Pure Virtual Function: Forces derived classes to define HOW they log a message.
    virtual void log(const string& message) const = 0; 
    
    // Abstract classes can still have regular methods and variables.
    void showLoggerType() const {
        cout << "[Logger Base] Initiating logging process..." << endl;
    }
    
    virtual ~Logger() = default; 
};

// --- Concrete Derived Class 1 ---
class FileLogger : public Logger {
public:
    // Implementation: Defines HOW FileLogger logs (to a pretend file).
    void log(const string& message) const override {
        cout << "FILE LOG: Successfully wrote '" << message << "' to log.txt" << endl;
    }
};

// --- Concrete Derived Class 2 ---
class ConsoleLogger : public Logger {
public:
    // Implementation: Defines HOW ConsoleLogger logs (to the screen).
    void log(const string& message) const override {
        cout << "CONSOLE OUTPUT: " << message << endl;
    }
};

int main() {
    // Logger baseLogger; // ERROR: Cannot instantiate abstract class!
    
    // We use a base class pointer to hold different derived objects.
    Logger* logHandler; 

    cout << "--- Logging to File ---" << endl;
    logHandler = new FileLogger();
    logHandler->showLoggerType(); // Call regular base method
    // Runtime Polymorphism: Calls FileLogger's specific implementation
    logHandler->log("Application starting up."); 
    delete logHandler;

    cout << "\n--- Logging to Console ---" << endl;
    logHandler = new ConsoleLogger();
    logHandler->showLoggerType();
    // Runtime Polymorphism: Calls ConsoleLogger's specific implementation
    logHandler->log("User login successful."); 
    delete logHandler;
    
    return 0;
}
\`\`\`

`,
    tags: [
      "C++",
      "Encapsulation",
      "Abstraction",
      "Getters",
      "Setters",
      "Data Hiding",
      "Abstract Classes",
      "Interfaces",
    ],
    date: "2024-10-14",
  },
  {
    id: 1015,
    topic: "c++",
    seriesKey: "c++",
    title: "File Handling",
    description:
      "Working with file streams for persistent data storage using ifstream, ofstream, and fstream.",
    content: `
##  File Handling in C++
C++ provides file stream classes in the ** fstream ** library for reading from and writing to files.
** 1.ofstream **
- Definition : Output File Stream
- Purpose : Used for writing data to a file (creating a file if it doesn't exist).
** 2.ifstream **
- Definition : Input File Stream
- Purpose : Used for reading data from a file.
** 3.fstream **
- Definition : File Stream
- Purpose : Used for both reading and writing to a file.
### Basic File Operations
File handling typically involves three steps: opening the file, performing read/write operations, and closing the file.
**1.Opening a File**
You create an object of one of the Fstream classes and pass the filename and the desired file mode as arguments.
- Syntax 
\`\`\`cpp
// For writing
ofstream file_out("example.txt", ios::out | ios::trunc);
\`\`\`
** File Mode Flag (Used with ios::) **
- ios::out :Opens file for writing.
- ios::in :Opens file for reading.
- ios::app :Appends output to the end of the file.
- ios::trunc :Deletes the contents of the file if it exists (Truncates).
- ios::binary:Opens the file in binary mode (for non-text data).
**2.Writing to a File (ofstream)**
You use the stream insertion operator (<<), similar to using cout.
- Syntax
\`\`\`cpp
file_out << "This line will be written to the file." << endl;
\`\`\`
**3.Reading from a File (ifstream)**
You use the stream extraction operator (>>), similar to using cin. You usually use a loop to read until the End-Of-File (EOF) is reached.
- Syntax
\`\`\`cpp
file_in >> variable; // Reads one word/token at a time
getline(file_in, line_variable); // Reads an entire line
\`\`\`
** 4. Closing a File **
It's crucial to explicitly close a file to ensure all buffered data is written to disk and resources are released.
- Syntax
\`\`\`cpp
file_out.close();
\`\`\`
### Simple Program Example (Read and Write)
This program demonstrates writing data to a file and then reading it back.
\`\`\`cpp
#include <iostream>
#include <fstream> // Required for file stream classes
#include <string>
using namespace std;

void writeFile() {
    // 1. Open file for writing. If 'data.txt' exists, 'ios::out' will overwrite it.
    ofstream file_out("data.txt"); 

    // Check if the file was successfully opened
    if (file_out.is_open()) {
        cout << "Writing data to file..." << endl;
        file_out << "C++ File Handling Example." << endl;
        file_out << "Line 2: Data from the program." << endl;
        file_out.close(); // 2. Close the file
    } else {
        cout << "Unable to open file for writing." << endl;
    }
}

void readFile() {
    // 1. Open file for reading
    ifstream file_in("data.txt"); 
    string line;

    if (file_in.is_open()) {
        cout << "\nReading data from file..." << endl;
        
        // Loop to read the file line by line until End-Of-File (EOF)
        while (getline(file_in, line)) {
            cout << "READ: " << line << endl;
        }
        
        file_in.close(); // 2. Close the file
    } else {
        cout << "Unable to open file for reading (File might not exist)." << endl;
    }
}

int main() {
    writeFile();
    readFile();
    
    return 0;
}
\`\`\`

`,
    tags: [
      "C++",
      "File Handling",
      "ifstream",
      "ofstream",
      "fstream",
      "File Operations",
      "Reading Files",
      "Writing Files",
    ],
    date: "2024-10-15",
  },
  {
    id: 1016,
    topic: "c++",
    seriesKey: "c++",
    title: "Templates in C++",
    description:
      "Creating generic functions and classes using templates for type-independent programming.",
    content: `
##  Templates in C++
A template is a blueprint or a formula for creating a generic class or function
- Goal: To achieve code reusability and type safety without sacrificing performance.
### Types of Templates
C++ supports two main types of templates:
** 1. Function Templates **
A function template defines a family of functions that can perform the same operation on different data types.
- Syntax
\`\`\`cpp
template <typename T>
T functionName(T arg1, T arg2) {
    // Function body using the generic type T
    return arg1 + arg2;
}
\`\`\`
** 2. Class Templates **
A class template defines a family of classes that can hold or manage data of any type. Standard Library containers like std::vector, std::map, and std::pair are implemented as class templates.
- Syntax 
\`\`\`cpp
template <class T> // 'typename' or 'class' can be used interchangeably here
class ClassName {
private:
    T data; // The class member uses the generic type T
public:
    ClassName(T val) : data(val) {}
    T getData() const {
        return data;
    }
};
\`\`\`
### Simple Program Example
This example demonstrates both a function template and a class template.
\`\`\`cpp
#include <iostream>
#include <string>
using namespace std;

// --- 1. Function Template Example ---
// The function 'getLarger' works for any type 'T' that supports comparison (>).
template <typename T>
T getLarger(T a, T b) {
    return (a > b) ? a : b;
}

// --- 2. Class Template Example ---
// The class 'Container' can hold any type 'T'.
template <class T>
class Container {
private:
    T value;
public:
    // Constructor
    Container(T v) : value(v) {}
    
    // Member function returning the generic type T
    T getValue() const {
        return value;
    }
};

int main() {
    // --- Function Template Instantiation ---
    
    // 1. Instantiation for integers (Compiler creates a version: int getLarger(int, int))
    int i = getLarger(10, 20);
    cout << "Larger integer: " << i << endl; 

    // 2. Instantiation for doubles (Compiler creates a version: double getLarger(double, double))
    double d = getLarger(3.14, 2.71);
    cout << "Larger double: " << d << endl; 

    // 3. Instantiation for strings (Comparison is lexicographical)
    string s = getLarger<string>("apple", "banana");
    cout << "Larger string: " << s << endl; 
    
    cout << "\n--------------------------" << endl;

    // --- Class Template Instantiation ---
    
    // 1. Instantiation for holding an integer
    Container<int> intBox(123); 
    cout << "Container holds int: " << intBox.getValue() << endl;

    // 2. Instantiation for holding a string
    Container<string> stringBox("Hello Templates");
    cout << "Container holds string: " << stringBox.getValue() << endl;

    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Templates",
      "Generic Programming",
      "Function Templates",
      "Class Templates",
      "Template Specialization",
      "Generic Classes",
    ],
    date: "2024-10-16",
  },
  {
    id: 1017,
    topic: "c++",
    seriesKey: "c++",
    title: "Exception Handling",
    description:
      "Using try-catch blocks and custom exceptions for robust error handling.",
    content: `
##  Exception Handling in C++
Exception handling allows programs to deal with unexpected situations gracefully without crashing.
### Key Components of Exception Handling 
C++ uses three keywords to manage exceptions: try, catch, and throw.
** 1. throw  ** 
The throw statement is used to signal an exceptional condition. When an error occurs, the program executes a throw statement, which stops the current function's execution and initiates the search for an exception handler.
- Syntax
\`\`\`cpp
throw exception_object;
// The exception_object can be an int, string, char*, or an object of a custom class.
\`\`\`
** 2. try **
A try block encloses the code segment where an exception might occur. If an exception is thrown within the try block (or from any function called within it), control immediately passes to the appropriate catch handler.
- Syntax
\`\`\`cpp
try {
    // Code that might throw an exception
}
\`\`\`
** 3. catch **
A catch block (the exception handler) immediately follows the try block. It specifies the type of exception it can handle. If the type of the thrown exception matches the type declared in the catch argument, the catch block is executed.
- Syntax 
\`\`\`cpp
catch (ExceptionType e) {
    // Code to handle the exception (e.g., logging, error message, recovery)
}
// Multiple catch blocks can follow a single try block.
\`\`\`
### Simple Program Example
This example demonstrates handling a division-by-zero error.
  \`\`\`cpp
#include <iostream>
#include <string>

double safeDivide(int numerator, int denominator) {
    // Check for the exceptional condition
    if (denominator == 0) {
        // 1. THROW: Signal the error using a string literal
        throw std::string("Error: Attempted division by zero."); 
    }
    return static_cast<double>(numerator) / denominator;
}

int main() {
    int x = 10;
    int y = 0;
    
    // 2. TRY: Enclose the potentially problematic code
    try {
        std::cout << "Attempting to divide " << x << " by " << y << std::endl;
        double result = safeDivide(x, y);
        std::cout << "Result: " << result << std::endl; // This line is skipped if exception is thrown
    } 
    // 3. CATCH: Handle the specific exception type (std::string)
    catch (std::string const& e) {
        // Output the error message from the thrown object
        std::cerr << "Exception Caught: " << e << std::endl; 
    }
    // Optional: Catch-all handler for any other unexpected exception type
    catch (...) {
        std::cerr << "An unknown exception occurred." << std::endl;
    }
    
    std::cout << "Program continues after exception handling." << std::endl;
    
    return 0;
}
  \`\`\`
  
`,
    tags: [
      "C++",
      "Exception Handling",
      "try-catch",
      "Custom Exceptions",
      "RAII",
      "Exception Safety",
      "runtime_error",
      "stdexcept",
    ],
    date: "2024-10-18",
  },
  {
    id: 1018,
    topic: "c++",
    seriesKey: "c++",
    title: "Namespaces in C++",
    description:
      "Using namespaces to organize code, avoid naming conflicts, and create modular programs.",
    content: `
##  Namespaces in C++
Namespaces in C++ are a mechanism designed to prevent name collisions by organizing code entities (like classes, functions, and variables) into distinct, logical scopes.
** Example: The std Namespace **
The most common namespace is std (the Standard namespace), which contains all the entities in the C++ Standard Library, such as cout, string, and vector.
### Syntax and Usage 
** 1. Defining a Namespace **
You define a namespace using the namespace keyword.
\`\`\`cpp
namespace MyCompany {
    // Variable declared within the namespace
    int id_counter = 0; 
    
    // Function declared within the namespace
    void process_data() {
        // ... code ...
    }
    
    // Class declared within the namespace
    class Employee {
        // ...
    };
}
\`\`\`
** 2. Accessing Namespace Members **
There are three main ways to access entities declared within a namespace:
** Scope Resolution Operator **
- Syntax : namespaceName::identifier
- Description : The safest way. Explicitly specifies which identifier to use every time.
** using Declaration **
- Syntax : using namespaceName::identifier;
- Description : Makes a single identifier available without the namespace prefix.
** using Directive **
- Syntax : using namespace namespaceName;
- Description : Makes all identifiers in the namespace available without the prefix (often discouraged in header files or large scopes due to pollution).
### Simple Program Example
This example shows how namespaces prevent ambiguity when two functions have the same name.
\`\`\`cpp
#include <iostream>
#include <string>

// Namespace 1: For geometry calculations
namespace Geometry {
    const double PI = 3.14159;
    
    double calculateArea(double radius) {
        return PI * radius * radius;
    }
}

// Namespace 2: For financial calculations
namespace Finance {
    const double PI = 0.05; // PI here represents Principal Interest Rate
    
    double calculateArea(double principal, double time) {
        return principal * PI * time;
    }
}

int main() {
    // Accessing members using the Scope Resolution Operator (::)
    
    // 1. Calling the area function from the Geometry namespace
    double circleArea = Geometry::calculateArea(5.0);
    std::cout << "Circle Area: " << circleArea << std::endl;
    
    // 2. Calling the area function from the Finance namespace
    double interest = Finance::calculateArea(1000.0, 2.0);
    std::cout << "Simple Interest: " << interest << std::endl;
    
    // 3. Demonstrating 'using' declaration for clarity
    using std::cout;
    using Finance::PI; // Imports Finance::PI into the current scope
    
    cout << "Finance PI (Interest Rate): " << PI << std::endl;
    // cout << "Geometry PI: " << PI; // ERROR: This would be ambiguous if we used 'using namespace Geometry;'

    return 0;
}
\`\`\`
### Inline Namespaces 
An inline namespace is marked with the inline keyword. It automatically makes its members appear as if they belong to the enclosing namespace, allowing access without the intermediate namespace qualifier.
** Purpose : **Primarily used for library versioning to enable newer versions of code to be accessed easily while maintaining backward compatibility.
### Anonymous Namespaces
An anonymous namespace is one declared without a name (namespace { ... }). Its identifiers are local to the current translation unit (source file).
** Purpose : **To prevent global names from colliding with names in other source files, effectively providing internal linkage for the contained entities (similar to using static on globals).
### Scope Resolution Operator (::)
The scope resolution operator (::) is used to explicitly specify which namespace, class, or global scope an identifier belongs to.
** Purpose : ** To resolve ambiguity and explicitly access names that might be hidden by local declarations or belong to a specific namespace (e.g., std::cout).
### Namespace Aliases
A namespace alias is a shorter, alternative name assigned to a longer or deeply nested namespace.
**Purpose : ** To improve code readability and reduce typing when referring to long namespace names (e.g., namespace FS = std::filesystem;).
### Code Organization
Namespaces serve as logical containers to group related functions, classes, and variables.
**Purpose : ** To structure large programs, prevent name collisions between different libraries or modules, and enhance the modularity of the codebase.

`,
    tags: [
      "C++",
      "Namespaces",
      "Scope Resolution",
      "Using Directive",
      "Namespace Aliases",
      "Inline Namespaces",
      "Anonymous Namespaces",
      "Code Organization",
    ],
    date: "2024-10-19",
  },
  {
    id: 1019,
    topic: "c++",
    seriesKey: "c++",
    title: "Project 1",
    description:
      "Building a complete console-based calculator with basic arithmetic operations.",
    content: `
##  Project 1: Simple Console Calculator
A complete implementation of a console calculator with basic arithmetic operations.

### Complete Calculator Implementation
\`\`\`cpp
#include <iostream>
#include <string>
#include <limits>
#include <cmath>
using namespace std;

namespace Calculator {
    // Display the calculator menu
    void displayMenu() {
        cout << "\\n=== SIMPLE CONSOLE CALCULATOR ===" << endl;
        cout << "1. Addition (+)" << endl;
        cout << "2. Subtraction (-)" << endl;
        cout << "3. Multiplication (*)" << endl;
        cout << "4. Division (/)" << endl;
        cout << "5. Modulus (%)" << endl;
        cout << "6. Power (^)" << endl;
        cout << "7. Square Root" << endl;
        cout << "8. Exit" << endl;
        cout << "==================================" << endl;
        cout << "Enter your choice (1-8): ";
    }
    
    // Get a valid double input from user
    double getNumberInput(const string& prompt) {
        double number;
        while (true) {
            cout << prompt;
            cin >> number;
            
            if (cin.fail()) {
                cin.clear();
                cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                cout << "Invalid input! Please enter a valid number." << endl;
            } else {
                cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                return number;
            }
        }
    }
    
    // Arithmetic operations
    double add(double a, double b) {
        return a + b;
    }
    
    double subtract(double a, double b) {
        return a - b;
    }
    
    double multiply(double a, double b) {
        return a * b;
    }
    
    double divide(double a, double b) {
        if (b == 0) {
            throw runtime_error("Error: Division by zero is not allowed!");
        }
        return a / b;
    }
    
    int modulus(int a, int b) {
        if (b == 0) {
            throw runtime_error("Error: Division by zero in modulus operation!");
        }
        return a % b;
    }
    
    double power(double base, double exponent) {
        return pow(base, exponent);
    }
    
    double squareRoot(double number) {
        if (number < 0) {
            throw runtime_error("Error: Cannot calculate square root of a negative number!");
        }
        return sqrt(number);
    }
    
    // Display result with formatting
    void displayResult(double result, const string& operation) {
        cout << "\\n" << operation << " = " << result << endl;
    }
    
    void displayResult(int result, const string& operation) {
        cout << "\\n" << operation << " = " << result << endl;
    }
    
    // Perform calculation based on user choice
    void performCalculation(int choice) {
        try {
            switch (choice) {
                case 1: { // Addition
                    double a = getNumberInput("Enter first number: ");
                    double b = getNumberInput("Enter second number: ");
                    double result = add(a, b);
                    displayResult(result, to_string(a) + " + " + to_string(b));
                    break;
                }
                case 2: { // Subtraction
                    double a = getNumberInput("Enter first number: ");
                    double b = getNumberInput("Enter second number: ");
                    double result = subtract(a, b);
                    displayResult(result, to_string(a) + " - " + to_string(b));
                    break;
                }
                case 3: { // Multiplication
                    double a = getNumberInput("Enter first number: ");
                    double b = getNumberInput("Enter second number: ");
                    double result = multiply(a, b);
                    displayResult(result, to_string(a) + " * " + to_string(b));
                    break;
                }
                case 4: { // Division
                    double a = getNumberInput("Enter first number: ");
                    double b = getNumberInput("Enter second number: ");
                    double result = divide(a, b);
                    displayResult(result, to_string(a) + " / " + to_string(b));
                    break;
                }
                case 5: { // Modulus
                    int a = static_cast<int>(getNumberInput("Enter first integer: "));
                    int b = static_cast<int>(getNumberInput("Enter second integer: "));
                    int result = modulus(a, b);
                    displayResult(result, to_string(a) + " % " + to_string(b));
                    break;
                }
                case 6: { // Power
                    double base = getNumberInput("Enter base: ");
                    double exponent = getNumberInput("Enter exponent: ");
                    double result = power(base, exponent);
                    displayResult(result, to_string(base) + " ^ " + to_string(exponent));
                    break;
                }
                case 7: { // Square Root
                    double number = getNumberInput("Enter number: ");
                    double result = squareRoot(number);
                    displayResult(result, "sqrt(" + to_string(number) + ")");
                    break;
                }
                default:
                    cout << "Invalid choice!" << endl;
            }
        }
        catch (const exception& e) {
            cout << e.what() << endl;
        }
    }
    
    // Get valid menu choice from user
    int getMenuChoice() {
        int choice;
        while (true) {
            displayMenu();
            cin >> choice;
            
            if (cin.fail() || choice < 1 || choice > 8) {
                cin.clear();
                cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                cout << "Invalid choice! Please enter a number between 1 and 8." << endl;
            } else {
                cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                return choice;
            }
        }
    }
    
    // Main calculator function
    void runCalculator() {
        cout << "Welcome to the Simple Console Calculator!" << endl;
        cout << "This calculator supports basic arithmetic operations." << endl;
        
        while (true) {
            int choice = getMenuChoice();
            
            if (choice == 8) {
                cout << "\\nThank you for using the calculator. Goodbye!" << endl;
                break;
            }
            
            performCalculation(choice);
            
            // Ask if user wants to continue
            cout << "\\nPress Enter to continue or 'q' to quit...";
            string input;
            getline(cin, input);
            if (input == "q" || input == "Q") {
                cout << "\\nThank you for using the calculator. Goodbye!" << endl;
                break;
            }
        }
    }
}

// Enhanced Calculator with History
namespace AdvancedCalculator {
    class CalculationHistory {
    private:
        vector<string> history;
        
    public:
        void addToHistory(const string& operation, double result) {
            string entry = operation + " = " + to_string(result);
            history.push_back(entry);
        }
        
        void displayHistory() const {
            if (history.empty()) {
                cout << "No calculations in history." << endl;
                return;
            }
            
            cout << "\\n=== CALCULATION HISTORY ===" << endl;
            for (size_t i = 0; i < history.size(); i++) {
                cout << i + 1 << ". " << history[i] << endl;
            }
        }
        
        void clearHistory() {
            history.clear();
            cout << "History cleared." << endl;
        }
    };
    
    class ScientificCalculator {
    private:
        CalculationHistory history;
        
    public:
        void run() {
            cout << "\\n=== SCIENTIFIC CALCULATOR ===" << endl;
            cout << "Additional features: History tracking" << endl;
            
            while (true) {
                cout << "\\nOptions: 1-8: Operations, 9: History, 10: Clear History, 11: Exit" << endl;
                cout << "Enter choice: ";
                
                int choice;
                cin >> choice;
                
                if (cin.fail()) {
                    cin.clear();
                    cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                    cout << "Invalid input!" << endl;
                    continue;
                }
                
                if (choice == 11) break;
                
                switch (choice) {
                    case 1: case 2: case 3: case 4: case 5: case 6: case 7:
                        Calculator::performCalculation(choice);
                        break;
                    case 9:
                        history.displayHistory();
                        break;
                    case 10:
                        history.clearHistory();
                        break;
                    default:
                        cout << "Invalid choice!" << endl;
                }
            }
        }
    };
}

// Main function
int main() {
    // Run basic calculator
    Calculator::runCalculator();
    
    // Uncomment to run advanced calculator
    // AdvancedCalculator::ScientificCalculator advancedCalc;
    // advancedCalc.run();
    
    return 0;
}
\`\`\`

### Calculator Features and Extensions
\`\`\`cpp
#include <iostream>
#include <vector>
#include <string>
#include <map>
#include <functional>
using namespace std;

// Extension: Calculator using function pointers and maps
namespace ExtendedCalculator {
    // Define operation function type
    using Operation = function<double(double, double)>;
    
    class SmartCalculator {
    private:
        map<string, Operation> operations;
        map<string, string> operationSymbols;
        
    public:
        SmartCalculator() {
            // Initialize operations
            operations["add"] = [](double a, double b) { return a + b; };
            operations["subtract"] = [](double a, double b) { return a - b; };
            operations["multiply"] = [](double a, double b) { return a * b; };
            operations["divide"] = [](double a, double b) { 
                if (b == 0) throw runtime_error("Division by zero");
                return a / b; 
            };
            
            operationSymbols["add"] = "+";
            operationSymbols["subtract"] = "-";
            operationSymbols["multiply"] = "*";
            operationSymbols["divide"] = "/";
        }
        
        void displayAvailableOperations() const {
            cout << "\\nAvailable operations:" << endl;
            for (const auto& op : operations) {
                cout << "- " << op.first << " (" << operationSymbols.at(op.first) << ")" << endl;
            }
        }
        
        double calculate(const string& operation, double a, double b) {
            if (operations.find(operation) == operations.end()) {
                throw invalid_argument("Unknown operation: " + operation);
            }
            return operations[operation](a, b);
        }
        
        void run() {
            cout << "\\n=== SMART CALCULATOR ===" << endl;
            displayAvailableOperations();
            
            while (true) {
                cout << "\\nEnter operation name (or 'quit' to exit): ";
                string operation;
                cin >> operation;
                
                if (operation == "quit") break;
                
                try {
                    cout << "Enter first number: ";
                    double a;
                    cin >> a;
                    
                    cout << "Enter second number: ";
                    double b;
                    cin >> b;
                    
                    double result = calculate(operation, a, b);
                    cout << a << " " << operationSymbols[operation] << " " << b << " = " << result << endl;
                }
                catch (const exception& e) {
                    cout << "Error: " << e.what() << endl;
                }
            }
        }
    };
}

// Extension: Unit Converter
namespace UnitConverter {
    class Converter {
    public:
        // Temperature conversions
        double celsiusToFahrenheit(double celsius) {
            return (celsius * 9/5) + 32;
        }
        
        double fahrenheitToCelsius(double fahrenheit) {
            return (fahrenheit - 32) * 5/9;
        }
        
        // Length conversions
        double metersToFeet(double meters) {
            return meters * 3.28084;
        }
        
        double feetToMeters(double feet) {
            return feet / 3.28084;
        }
        
        // Weight conversions
        double kilogramsToPounds(double kg) {
            return kg * 2.20462;
        }
        
        double poundsToKilograms(double pounds) {
            return pounds / 2.20462;
        }
        
        void run() {
            cout << "\\n=== UNIT CONVERTER ===" << endl;
            cout << "1. Celsius to Fahrenheit" << endl;
            cout << "2. Fahrenheit to Celsius" << endl;
            cout << "3. Meters to Feet" << endl;
            cout << "4. Feet to Meters" << endl;
            cout << "5. Kilograms to Pounds" << endl;
            cout << "6. Pounds to Kilograms" << endl;
            
            int choice;
            double value;
            
            cout << "Enter choice: ";
            cin >> choice;
            
            cout << "Enter value: ";
            cin >> value;
            
            double result;
            string conversion;
            
            switch (choice) {
                case 1:
                    result = celsiusToFahrenheit(value);
                    conversion = to_string(value) + "°C = " + to_string(result) + "°F";
                    break;
                case 2:
                    result = fahrenheitToCelsius(value);
                    conversion = to_string(value) + "°F = " + to_string(result) + "°C";
                    break;
                case 3:
                    result = metersToFeet(value);
                    conversion = to_string(value) + "m = " + to_string(result) + "ft";
                    break;
                case 4:
                    result = feetToMeters(value);
                    conversion = to_string(value) + "ft = " + to_string(result) + "m";
                    break;
                case 5:
                    result = kilogramsToPounds(value);
                    conversion = to_string(value) + "kg = " + to_string(result) + "lb";
                    break;
                case 6:
                    result = poundsToKilograms(value);
                    conversion = to_string(value) + "lb = " + to_string(result) + "kg";
                    break;
                default:
                    cout << "Invalid choice!" << endl;
                    return;
            }
            
            cout << conversion << endl;
        }
    };
}

int main() {
    // Run basic calculator
    Calculator::runCalculator();
    
    // Uncomment to run extended calculators
    /*
    ExtendedCalculator::SmartCalculator smartCalc;
    smartCalc.run();
    
    UnitConverter::Converter converter;
    converter.run();
    */
    
    return 0;
}
\`\`\`
`,
    tags: [
      "C++",
      "Project",
      "Calculator",
      "Console Application",
      "Arithmetic Operations",
      "Error Handling",
      "Menu System",
      "User Input",
    ],
    date: "2024-10-20",
  },
  {
    id: 1020,
    topic: "c++",
    seriesKey: "c++",
    title: "Project 2",
    description: "Building a complete library management system using OOP, file handling, and STL.",
    content: `
##  Project 2: Library Management System
A comprehensive library management system demonstrating OOP principles, file handling, and STL usage.

### Complete Library Management System
\`\`\`cpp
#include <iostream>
#include <vector>
#include <string>
#include <map>
#include <set>
#include <fstream>
#include <algorithm>
#include <memory>
#include <iomanip>
using namespace std;

namespace LibrarySystem {
    // Forward declarations
    class Book;
    class Member;
    class Librarian;
    
    // Custom exceptions
    class LibraryException : public exception {
    private:
        string message;
    public:
        LibraryException(const string& msg) : message(msg) {}
        const char* what() const noexcept override { return message.c_str(); }
    };
    
    class BookNotFoundException : public LibraryException {
    public:
        BookNotFoundException(const string& isbn) 
            : LibraryException("Book with ISBN " + isbn + " not found!") {}
    };
    
    class MemberNotFoundException : public LibraryException {
    public:
        MemberNotFoundException(int memberId) 
            : LibraryException("Member with ID " + to_string(memberId) + " not found!") {}
    };

    // Book class
    class Book {
    private:
        string isbn;
        string title;
        string author;
        string genre;
        int publicationYear;
        bool isAvailable;
        int borrowedBy; // Member ID who borrowed the book

    public:
        Book(const string& i, const string& t, const string& a, 
             const string& g, int year)
            : isbn(i), title(t), author(a), genre(g), 
              publicationYear(year), isAvailable(true), borrowedBy(-1) {}
        
        // Getters
        string getISBN() const { return isbn; }
        string getTitle() const { return title; }
        string getAuthor() const { return author; }
        string getGenre() const { return genre; }
        int getPublicationYear() const { return publicationYear; }
        bool getIsAvailable() const { return isAvailable; }
        int getBorrowedBy() const { return borrowedBy; }
        
        // Setters
        void setAvailable(bool available) { isAvailable = available; }
        void setBorrowedBy(int memberId) { borrowedBy = memberId; }
        
        // Display book information
        void display() const {
            cout << "ISBN: " << isbn << endl;
            cout << "Title: " << title << endl;
            cout << "Author: " << author << endl;
            cout << "Genre: " << genre << endl;
            cout << "Year: " << publicationYear << endl;
            cout << "Status: " << (isAvailable ? "Available" : "Borrowed by Member #" + to_string(borrowedBy)) << endl;
            cout << "----------------------------------------" << endl;
        }
        
        // Save book to file
        string toFileString() const {
            return isbn + "," + title + "," + author + "," + genre + "," +
                   to_string(publicationYear) + "," + 
                   (isAvailable ? "1" : "0") + "," + to_string(borrowedBy);
        }
        
        // Load book from file
        static Book fromFileString(const string& data) {
            vector<string> tokens;
            string token;
            for (char c : data) {
                if (c == ',') {
                    tokens.push_back(token);
                    token.clear();
                } else {
                    token += c;
                }
            }
            tokens.push_back(token);
            
            Book book(tokens[0], tokens[1], tokens[2], tokens[3], stoi(tokens[4]));
            book.isAvailable = (tokens[5] == "1");
            book.borrowedBy = stoi(tokens[6]);
            return book;
        }
    };

    // Member class
    class Member {
    private:
        int memberId;
        string name;
        string email;
        string phone;
        vector<string> borrowedBooks; // ISBNs of borrowed books
        static int nextMemberId;

    public:
        Member(const string& n, const string& e, const string& p)
            : memberId(nextMemberId++), name(n), email(e), phone(p) {}
        
        Member(int id, const string& n, const string& e, const string& p)
            : memberId(id), name(n), email(e), phone(p) {
            if (id >= nextMemberId) nextMemberId = id + 1;
        }
        
        // Getters
        int getMemberId() const { return memberId; }
        string getName() const { return name; }
        string getEmail() const { return email; }
        string getPhone() const { return phone; }
        const vector<string>& getBorrowedBooks() const { return borrowedBooks; }
        
        // Book management
        void borrowBook(const string& isbn) {
            if (borrowedBooks.size() >= 5) {
                throw LibraryException("Member cannot borrow more than 5 books!");
            }
            borrowedBooks.push_back(isbn);
        }
        
        void returnBook(const string& isbn) {
            auto it = find(borrowedBooks.begin(), borrowedBooks.end(), isbn);
            if (it != borrowedBooks.end()) {
                borrowedBooks.erase(it);
            } else {
                throw LibraryException("Book with ISBN " + isbn + " not found in member's borrowed list!");
            }
        }
        
        // Display member information
        void display() const {
            cout << "Member ID: " << memberId << endl;
            cout << "Name: " << name << endl;
            cout << "Email: " << email << endl;
            cout << "Phone: " << phone << endl;
            cout << "Borrowed Books: " << borrowedBooks.size() << endl;
            for (const auto& isbn : borrowedBooks) {
                cout << "  - ISBN: " << isbn << endl;
            }
            cout << "----------------------------------------" << endl;
        }
        
        // Save member to file
        string toFileString() const {
            string result = to_string(memberId) + "," + name + "," + email + "," + phone;
            for (const auto& isbn : borrowedBooks) {
                result += "," + isbn;
            }
            return result;
        }
        
        // Load member from file
        static Member fromFileString(const string& data) {
            vector<string> tokens;
            string token;
            for (char c : data) {
                if (c == ',') {
                    tokens.push_back(token);
                    token.clear();
                } else {
                    token += c;
                }
            }
            tokens.push_back(token);
            
            Member member(stoi(tokens[0]), tokens[1], tokens[2], tokens[3]);
            
            // Add borrowed books (if any)
            for (size_t i = 4; i < tokens.size(); i++) {
                member.borrowedBooks.push_back(tokens[i]);
            }
            
            return member;
        }
    };

    // Initialize static member
    int Member::nextMemberId = 1001;

    // Library class - main system
    class Library {
    private:
        map<string, Book> books; // Key: ISBN
        map<int, Member> members; // Key: Member ID
        string booksFilename;
        string membersFilename;

    public:
        Library(const string& bFile = "books.dat", const string& mFile = "members.dat")
            : booksFilename(bFile), membersFilename(mFile) {
            loadData();
        }
        
        ~Library() {
            saveData();
        }
        
        // Book management
        void addBook(const Book& book) {
            if (books.find(book.getISBN()) != books.end()) {
                throw LibraryException("Book with ISBN " + book.getISBN() + " already exists!");
            }
            books[book.getISBN()] = book;
            cout << "Book added successfully!" << endl;
        }
        
        void removeBook(const string& isbn) {
            auto it = books.find(isbn);
            if (it == books.end()) {
                throw BookNotFoundException(isbn);
            }
            if (!it->second.getIsAvailable()) {
                throw LibraryException("Cannot remove book that is currently borrowed!");
            }
            books.erase(it);
            cout << "Book removed successfully!" << endl;
        }
        
        Book& findBook(const string& isbn) {
            auto it = books.find(isbn);
            if (it == books.end()) {
                throw BookNotFoundException(isbn);
            }
            return it->second;
        }
        
        // Member management
        int addMember(const string& name, const string& email, const string& phone) {
            Member member(name, email, phone);
            members[member.getMemberId()] = member;
            cout << "Member added with ID: " << member.getMemberId() << endl;
            return member.getMemberId();
        }
        
        void removeMember(int memberId) {
            auto it = members.find(memberId);
            if (it == members.end()) {
                throw MemberNotFoundException(memberId);
            }
            if (!it->second.getBorrowedBooks().empty()) {
                throw LibraryException("Cannot remove member with borrowed books!");
            }
            members.erase(it);
            cout << "Member removed successfully!" << endl;
        }
        
        Member& findMember(int memberId) {
            auto it = members.find(memberId);
            if (it == members.end()) {
                throw MemberNotFoundException(memberId);
            }
            return it->second;
        }
        
        // Book borrowing and returning
        void borrowBook(int memberId, const string& isbn) {
            Member& member = findMember(memberId);
            Book& book = findBook(isbn);
            
            if (!book.getIsAvailable()) {
                throw LibraryException("Book is not available for borrowing!");
            }
            
            member.borrowBook(isbn);
            book.setAvailable(false);
            book.setBorrowedBy(memberId);
            
            cout << "Book borrowed successfully!" << endl;
        }
        
        void returnBook(int memberId, const string& isbn) {
            Member& member = findMember(memberId);
            Book& book = findBook(isbn);
            
            member.returnBook(isbn);
            book.setAvailable(true);
            book.setBorrowedBy(-1);
            
            cout << "Book returned successfully!" << endl;
        }
        
        // Search and display functions
        void displayAllBooks() const {
            if (books.empty()) {
                cout << "No books in library!" << endl;
                return;
            }
            
            cout << "\\n=== ALL BOOKS ===" << endl;
            for (const auto& pair : books) {
                pair.second.display();
            }
        }
        
        void displayAllMembers() const {
            if (members.empty()) {
                cout << "No members registered!" << endl;
                return;
            }
            
            cout << "\\n=== ALL MEMBERS ===" << endl;
            for (const auto& pair : members) {
                pair.second.display();
            }
        }
        
        void searchBooksByTitle(const string& title) const {
            cout << "\\n=== SEARCH RESULTS FOR: " << title << " ===" << endl;
            bool found = false;
            for (const auto& pair : books) {
                if (pair.second.getTitle().find(title) != string::npos) {
                    pair.second.display();
                    found = true;
                }
            }
            if (!found) {
                cout << "No books found with title containing: " << title << endl;
            }
        }
        
        void searchBooksByAuthor(const string& author) const {
            cout << "\\n=== SEARCH RESULTS FOR AUTHOR: " << author << " ===" << endl;
            bool found = false;
            for (const auto& pair : books) {
                if (pair.second.getAuthor().find(author) != string::npos) {
                    pair.second.display();
                    found = true;
                }
            }
            if (!found) {
                cout << "No books found by author: " << author << endl;
            }
        }
        
        // File operations
        void saveData() {
            // Save books
            ofstream bookFile(booksFilename);
            if (bookFile.is_open()) {
                for (const auto& pair : books) {
                    bookFile << pair.second.toFileString() << endl;
                }
                bookFile.close();
            }
            
            // Save members
            ofstream memberFile(membersFilename);
            if (memberFile.is_open()) {
                for (const auto& pair : members) {
                    memberFile << pair.second.toFileString() << endl;
                }
                memberFile.close();
            }
        }
        
        void loadData() {
            // Load books
            ifstream bookFile(booksFilename);
            if (bookFile.is_open()) {
                string line;
                while (getline(bookFile, line)) {
                    if (!line.empty()) {
                        Book book = Book::fromFileString(line);
                        books[book.getISBN()] = book;
                    }
                }
                bookFile.close();
            }
            
            // Load members
            ifstream memberFile(membersFilename);
            if (memberFile.is_open()) {
                string line;
                while (getline(memberFile, line)) {
                    if (!line.empty()) {
                        Member member = Member::fromFileString(line);
                        members[member.getMemberId()] = member;
                    }
                }
                memberFile.close();
            }
        }
        
        // Statistics
        void displayStatistics() const {
            cout << "\\n=== LIBRARY STATISTICS ===" << endl;
            cout << "Total Books: " << books.size() << endl;
            cout << "Total Members: " << members.size() << endl;
            
            int availableBooks = 0;
            int borrowedBooks = 0;
            for (const auto& pair : books) {
                if (pair.second.getIsAvailable()) {
                    availableBooks++;
                } else {
                    borrowedBooks++;
                }
            }
            
            cout << "Available Books: " << availableBooks << endl;
            cout << "Borrowed Books: " << borrowedBooks << endl;
            
            // Most borrowed books
            map<string, int> genreCount;
            for (const auto& pair : books) {
                genreCount[pair.second.getGenre()]++;
            }
            
            if (!genreCount.empty()) {
                auto mostCommonGenre = max_element(genreCount.begin(), genreCount.end(),
                    [](const auto& a, const auto& b) { return a.second < b.second; });
                cout << "Most Common Genre: " << mostCommonGenre->first 
                     << " (" << mostCommonGenre->second << " books)" << endl;
            }
        }
    };

    // User Interface class
    class LibraryUI {
    private:
        Library library;

    public:
        void displayMainMenu() {
            cout << "\\n=== LIBRARY MANAGEMENT SYSTEM ===" << endl;
            cout << "1. Book Management" << endl;
            cout << "2. Member Management" << endl;
            cout << "3. Borrow/Return Books" << endl;
            cout << "4. Search Books" << endl;
            cout << "5. Display All" << endl;
            cout << "6. Statistics" << endl;
            cout << "7. Exit" << endl;
            cout << "==================================" << endl;
            cout << "Enter your choice (1-7): ";
        }
        
        void run() {
            cout << "Welcome to the Library Management System!" << endl;
            
            while (true) {
                try {
                    displayMainMenu();
                    int choice;
                    cin >> choice;
                    cin.ignore(); // Clear newline
                    
                    switch (choice) {
                        case 1: bookManagementMenu(); break;
                        case 2: memberManagementMenu(); break;
                        case 3: borrowReturnMenu(); break;
                        case 4: searchMenu(); break;
                        case 5: displayAllMenu(); break;
                        case 6: library.displayStatistics(); break;
                        case 7: 
                            cout << "Thank you for using the Library Management System!" << endl;
                            return;
                        default:
                            cout << "Invalid choice! Please try again." << endl;
                    }
                }
                catch (const exception& e) {
                    cout << "Error: " << e.what() << endl;
                }
            }
        }
        
    private:
        void bookManagementMenu() {
            cout << "\\n=== BOOK MANAGEMENT ===" << endl;
            cout << "1. Add Book" << endl;
            cout << "2. Remove Book" << endl;
            cout << "3. Back to Main Menu" << endl;
            cout << "Enter choice: ";
            
            int choice;
            cin >> choice;
            cin.ignore();
            
            switch (choice) {
                case 1: {
                    string isbn, title, author, genre;
                    int year;
                    
                    cout << "Enter ISBN: "; getline(cin, isbn);
                    cout << "Enter Title: "; getline(cin, title);
                    cout << "Enter Author: "; getline(cin, author);
                    cout << "Enter Genre: "; getline(cin, genre);
                    cout << "Enter Publication Year: "; cin >> year;
                    
                    Book book(isbn, title, author, genre, year);
                    library.addBook(book);
                    break;
                }
                case 2: {
                    string isbn;
                    cout << "Enter ISBN to remove: "; getline(cin, isbn);
                    library.removeBook(isbn);
                    break;
                }
                case 3:
                    return;
                default:
                    cout << "Invalid choice!" << endl;
            }
        }
        
        void memberManagementMenu() {
            cout << "\\n=== MEMBER MANAGEMENT ===" << endl;
            cout << "1. Add Member" << endl;
            cout << "2. Remove Member" << endl;
            cout << "3. Back to Main Menu" << endl;
            cout << "Enter choice: ";
            
            int choice;
            cin >> choice;
            cin.ignore();
            
            switch (choice) {
                case 1: {
                    string name, email, phone;
                    
                    cout << "Enter Name: "; getline(cin, name);
                    cout << "Enter Email: "; getline(cin, email);
                    cout << "Enter Phone: "; getline(cin, phone);
                    
                    library.addMember(name, email, phone);
                    break;
                }
                case 2: {
                    int memberId;
                    cout << "Enter Member ID to remove: "; cin >> memberId;
                    library.removeMember(memberId);
                    break;
                }
                case 3:
                    return;
                default:
                    cout << "Invalid choice!" << endl;
            }
        }
        
        void borrowReturnMenu() {
            cout << "\\n=== BORROW/RETURN ===" << endl;
            cout << "1. Borrow Book" << endl;
            cout << "2. Return Book" << endl;
            cout << "3. Back to Main Menu" << endl;
            cout << "Enter choice: ";
            
            int choice;
            cin >> choice;
            
            switch (choice) {
                case 1: {
                    int memberId;
                    string isbn;
                    cout << "Enter Member ID: "; cin >> memberId;
                    cout << "Enter ISBN: "; cin >> isbn;
                    library.borrowBook(memberId, isbn);
                    break;
                }
                case 2: {
                    int memberId;
                    string isbn;
                    cout << "Enter Member ID: "; cin >> memberId;
                    cout << "Enter ISBN: "; cin >> isbn;
                    library.returnBook(memberId, isbn);
                    break;
                }
                case 3:
                    return;
                default:
                    cout << "Invalid choice!" << endl;
            }
        }
        
        void searchMenu() {
            cout << "\\n=== SEARCH BOOKS ===" << endl;
            cout << "1. Search by Title" << endl;
            cout << "2. Search by Author" << endl;
            cout << "3. Back to Main Menu" << endl;
            cout << "Enter choice: ";
            
            int choice;
            cin >> choice;
            cin.ignore();
            
            switch (choice) {
                case 1: {
                    string title;
                    cout << "Enter title to search: "; getline(cin, title);
                    library.searchBooksByTitle(title);
                    break;
                }
                case 2: {
                    string author;
                    cout << "Enter author to search: "; getline(cin, author);
                    library.searchBooksByAuthor(author);
                    break;
                }
                case 3:
                    return;
                default:
                    cout << "Invalid choice!" << endl;
            }
        }
        
        void displayAllMenu() {
            cout << "\\n=== DISPLAY ALL ===" << endl;
            cout << "1. Display All Books" << endl;
            cout << "2. Display All Members" << endl;
            cout << "3. Back to Main Menu" << endl;
            cout << "Enter choice: ";
            
            int choice;
            cin >> choice;
            
            switch (choice) {
                case 1: library.displayAllBooks(); break;
                case 2: library.displayAllMembers(); break;
                case 3: return;
                default: cout << "Invalid choice!" << endl;
            }
        }
    };
}

int main() {
    LibrarySystem::LibraryUI libraryUI;
    libraryUI.run();
    return 0;
}
\`\`\`
`,
    tags: ["C++", "Project", "Library Management", "OOP", "File Handling", "STL", "Exception Handling", "Class Design"],
    date: "2024-10-21",
  },
  {
    id: 1021,
    topic: "c++",
    seriesKey: "c++",
    title: "Homework",
    description: "Extending the calculator project with scientific functions and advanced features.",
    content: `
## 🔬 Homework: Scientific Calculator Extension
Extending the basic calculator with scientific functions, constants, and advanced mathematical operations.

### Scientific Calculator Implementation
\`\`\`cpp
#include <iostream>
#include <cmath>
#include <vector>
#include <string>
#include <map>
#include <functional>
#include <iomanip>
#include <limits>
using namespace std;

namespace ScientificCalculator {
    // Constants
    const double PI = 3.14159265358979323846;
    const double E = 2.71828182845904523536;
    
    class ScientificCalc {
    private:
        vector<string> history;
        double memory;
        
    public:
        ScientificCalc() : memory(0.0) {}
        
        // Basic arithmetic operations
        double add(double a, double b) { return a + b; }
        double subtract(double a, double b) { return a - b; }
        double multiply(double a, double b) { return a * b; }
        double divide(double a, double b) { 
            if (b == 0) throw runtime_error("Division by zero!");
            return a / b; 
        }
        
        // Scientific functions
        double sine(double angle, bool radians = true) {
            if (!radians) angle = degreesToRadians(angle);
            return sin(angle);
        }
        
        double cosine(double angle, bool radians = true) {
            if (!radians) angle = degreesToRadians(angle);
            return cos(angle);
        }
        
        double tangent(double angle, bool radians = true) {
            if (!radians) angle = degreesToRadians(angle);
            return tan(angle);
        }
        
        double logarithm(double x, double base = 10.0) {
            if (x <= 0) throw runtime_error("Logarithm undefined for non-positive numbers!");
            if (base <= 0 || base == 1) throw runtime_error("Invalid logarithm base!");
            return log(x) / log(base);
        }
        
        double naturalLog(double x) {
            if (x <= 0) throw runtime_error("Natural log undefined for non-positive numbers!");
            return log(x);
        }
        
        double power(double base, double exponent) {
            return pow(base, exponent);
        }
        
        double squareRoot(double x) {
            if (x < 0) throw runtime_error("Square root undefined for negative numbers!");
            return sqrt(x);
        }
        
        double factorial(int n) {
            if (n < 0) throw runtime_error("Factorial undefined for negative numbers!");
            if (n == 0 || n == 1) return 1;
            
            double result = 1;
            for (int i = 2; i <= n; i++) {
                result *= i;
            }
            return result;
        }
        
        // Conversion functions
        double degreesToRadians(double degrees) {
            return degrees * PI / 180.0;
        }
        
        double radiansToDegrees(double radians) {
            return radians * 180.0 / PI;
        }
        
        // Memory operations
        void memoryStore(double value) { memory = value; }
        double memoryRecall() { return memory; }
        void memoryClear() { memory = 0.0; }
        void memoryAdd(double value) { memory += value; }
        void memorySubtract(double value) { memory -= value; }
        
        // History management
        void addToHistory(const string& operation) {
            history.push_back(operation);
            // Keep only last 10 operations
            if (history.size() > 10) {
                history.erase(history.begin());
            }
        }
        
        void displayHistory() const {
            if (history.empty()) {
                cout << "No history available." << endl;
                return;
            }
            
            cout << "\\n=== CALCULATION HISTORY ===" << endl;
            for (size_t i = 0; i < history.size(); i++) {
                cout << i + 1 << ". " << history[i] << endl;
            }
        }
        
        void clearHistory() {
            history.clear();
            cout << "History cleared." << endl;
        }
        
        // Statistical functions
        double mean(const vector<double>& numbers) {
            if (numbers.empty()) throw runtime_error("No numbers provided!");
            
            double sum = 0;
            for (double num : numbers) {
                sum += num;
            }
            return sum / numbers.size();
        }
        
        double standardDeviation(const vector<double>& numbers) {
            if (numbers.size() < 2) throw runtime_error("At least 2 numbers required!");
            
            double m = mean(numbers);
            double sumSquaredDifferences = 0;
            
            for (double num : numbers) {
                sumSquaredDifferences += pow(num - m, 2);
            }
            
            return sqrt(sumSquaredDifferences / (numbers.size() - 1));
        }
    };
    
    // User Interface
    class ScientificCalcUI {
    private:
        ScientificCalc calculator;
        
    public:
        void run() {
            cout << "=== SCIENTIFIC CALCULATOR ===" << endl;
            cout << "Features: Basic Math, Trigonometry, Logarithms, Statistics, Memory" << endl;
            
            while (true) {
                displayMainMenu();
                int choice = getMenuChoice();
                
                if (choice == 0) {
                    cout << "Thank you for using the Scientific Calculator!" << endl;
                    break;
                }
                
                try {
                    processChoice(choice);
                }
                catch (const exception& e) {
                    cout << "Error: " << e.what() << endl;
                }
                
                cout << "\\nPress Enter to continue...";
                cin.ignore();
                cin.get();
            }
        }
        
    private:
        void displayMainMenu() {
            cout << "\\n=== MAIN MENU ===" << endl;
            cout << "1. Basic Arithmetic" << endl;
            cout << "2. Trigonometric Functions" << endl;
            cout << "3. Logarithms and Exponents" << endl;
            cout << "4. Statistical Functions" << endl;
            cout << "5. Memory Operations" << endl;
            cout << "6. View History" << endl;
            cout << "7. Clear History" << endl;
            cout << "0. Exit" << endl;
            cout << "==================" << endl;
            cout << "Enter your choice: ";
        }
        
        int getMenuChoice() {
            int choice;
            cin >> choice;
            if (cin.fail()) {
                cin.clear();
                cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                return -1;
            }
            cin.ignore(numeric_limits<streamsize>::max(), '\\n');
            return choice;
        }
        
        double getNumberInput(const string& prompt) {
            double number;
            cout << prompt;
            cin >> number;
            if (cin.fail()) {
                cin.clear();
                cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                throw runtime_error("Invalid number input!");
            }
            cin.ignore(numeric_limits<streamsize>::max(), '\\n');
            return number;
        }
        
        void processChoice(int choice) {
            switch (choice) {
                case 1: basicArithmeticMenu(); break;
                case 2: trigonometryMenu(); break;
                case 3: logarithmsMenu(); break;
                case 4: statisticsMenu(); break;
                case 5: memoryMenu(); break;
                case 6: calculator.displayHistory(); break;
                case 7: calculator.clearHistory(); break;
                default: cout << "Invalid choice!" << endl;
            }
        }
        
        void basicArithmeticMenu() {
            cout << "\\n=== BASIC ARITHMETIC ===" << endl;
            cout << "1. Addition" << endl;
            cout << "2. Subtraction" << endl;
            cout << "3. Multiplication" << endl;
            cout << "4. Division" << endl;
            cout << "5. Power" << endl;
            cout << "6. Square Root" << endl;
            cout << "7. Factorial" << endl;
            cout << "Enter choice: ";
            
            int opChoice;
            cin >> opChoice;
            
            try {
                switch (opChoice) {
                    case 1: {
                        double a = getNumberInput("Enter first number: ");
                        double b = getNumberInput("Enter second number: ");
                        double result = calculator.add(a, b);
                        string operation = to_string(a) + " + " + to_string(b) + " = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 2: {
                        double a = getNumberInput("Enter first number: ");
                        double b = getNumberInput("Enter second number: ");
                        double result = calculator.subtract(a, b);
                        string operation = to_string(a) + " - " + to_string(b) + " = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 3: {
                        double a = getNumberInput("Enter first number: ");
                        double b = getNumberInput("Enter second number: ");
                        double result = calculator.multiply(a, b);
                        string operation = to_string(a) + " * " + to_string(b) + " = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 4: {
                        double a = getNumberInput("Enter first number: ");
                        double b = getNumberInput("Enter second number: ");
                        double result = calculator.divide(a, b);
                        string operation = to_string(a) + " / " + to_string(b) + " = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 5: {
                        double base = getNumberInput("Enter base: ");
                        double exponent = getNumberInput("Enter exponent: ");
                        double result = calculator.power(base, exponent);
                        string operation = to_string(base) + " ^ " + to_string(exponent) + " = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 6: {
                        double x = getNumberInput("Enter number: ");
                        double result = calculator.squareRoot(x);
                        string operation = "sqrt(" + to_string(x) + ") = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 7: {
                        int n = static_cast<int>(getNumberInput("Enter integer: "));
                        double result = calculator.factorial(n);
                        string operation = to_string(n) + "! = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    default: cout << "Invalid operation choice!" << endl;
                }
            }
            catch (const exception& e) {
                throw;
            }
        }
        
        void trigonometryMenu() {
            cout << "\\n=== TRIGONOMETRIC FUNCTIONS ===" << endl;
            cout << "1. Sine" << endl;
            cout << "2. Cosine" << endl;
            cout << "3. Tangent" << endl;
            cout << "4. Degrees to Radians" << endl;
            cout << "5. Radians to Degrees" << endl;
            cout << "Enter choice: ";
            
            int opChoice;
            cin >> opChoice;
            
            cout << "Use radians? (1 for Yes, 0 for No): ";
            bool useRadians;
            cin >> useRadians;
            
            try {
                switch (opChoice) {
                    case 1: {
                        double angle = getNumberInput("Enter angle: ");
                        double result = calculator.sine(angle, useRadians);
                        string unit = useRadians ? "rad" : "deg";
                        string operation = "sin(" + to_string(angle) + unit + ") = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 2: {
                        double angle = getNumberInput("Enter angle: ");
                        double result = calculator.cosine(angle, useRadians);
                        string unit = useRadians ? "rad" : "deg";
                        string operation = "cos(" + to_string(angle) + unit + ") = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 3: {
                        double angle = getNumberInput("Enter angle: ");
                        double result = calculator.tangent(angle, useRadians);
                        string unit = useRadians ? "rad" : "deg";
                        string operation = "tan(" + to_string(angle) + unit + ") = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 4: {
                        double degrees = getNumberInput("Enter degrees: ");
                        double radians = calculator.degreesToRadians(degrees);
                        string operation = to_string(degrees) + "° = " + to_string(radians) + " rad";
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 5: {
                        double radians = getNumberInput("Enter radians: ");
                        double degrees = calculator.radiansToDegrees(radians);
                        string operation = to_string(radians) + " rad = " + to_string(degrees) + "°";
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    default: cout << "Invalid operation choice!" << endl;
                }
            }
            catch (const exception& e) {
                throw;
            }
        }
        
        void logarithmsMenu() {
            cout << "\\n=== LOGARITHMS AND EXPONENTS ===" << endl;
            cout << "1. Logarithm (base 10)" << endl;
            cout << "2. Natural Logarithm (base e)" << endl;
            cout << "3. Custom Base Logarithm" << endl;
            cout << "Enter choice: ";
            
            int opChoice;
            cin >> opChoice;
            
            try {
                switch (opChoice) {
                    case 1: {
                        double x = getNumberInput("Enter number: ");
                        double result = calculator.logarithm(x);
                        string operation = "log10(" + to_string(x) + ") = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 2: {
                        double x = getNumberInput("Enter number: ");
                        double result = calculator.naturalLog(x);
                        string operation = "ln(" + to_string(x) + ") = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 3: {
                        double x = getNumberInput("Enter number: ");
                        double base = getNumberInput("Enter base: ");
                        double result = calculator.logarithm(x, base);
                        string operation = "log_" + to_string(base) + "(" + to_string(x) + ") = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    default: cout << "Invalid operation choice!" << endl;
                }
            }
            catch (const exception& e) {
                throw;
            }
        }
        
        void statisticsMenu() {
            cout << "\\n=== STATISTICAL FUNCTIONS ===" << endl;
            cout << "1. Mean" << endl;
            cout << "2. Standard Deviation" << endl;
            cout << "Enter choice: ";
            
            int opChoice;
            cin >> opChoice;
            
            try {
                vector<double> numbers;
                cout << "Enter numbers (enter 'done' when finished):" << endl;
                
                string input;
                while (true) {
                    cout << "Number: ";
                    cin >> input;
                    if (input == "done") break;
                    
                    try {
                        double num = stod(input);
                        numbers.push_back(num);
                    }
                    catch (const exception&) {
                        cout << "Invalid number! Enter 'done' to finish." << endl;
                    }
                }
                
                switch (opChoice) {
                    case 1: {
                        double result = calculator.mean(numbers);
                        string operation = "mean of " + to_string(numbers.size()) + " numbers = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    case 2: {
                        double result = calculator.standardDeviation(numbers);
                        string operation = "std dev of " + to_string(numbers.size()) + " numbers = " + to_string(result);
                        calculator.addToHistory(operation);
                        cout << operation << endl;
                        break;
                    }
                    default: cout << "Invalid operation choice!" << endl;
                }
            }
            catch (const exception& e) {
                throw;
            }
        }
        
        void memoryMenu() {
            cout << "\\n=== MEMORY OPERATIONS ===" << endl;
            cout << "1. Store to Memory" << endl;
            cout << "2. Recall from Memory" << endl;
            cout << "3. Clear Memory" << endl;
            cout << "4. Add to Memory" << endl;
            cout << "5. Subtract from Memory" << endl;
            cout << "Enter choice: ";
            
            int opChoice;
            cin >> opChoice;
            
            try {
                switch (opChoice) {
                    case 1: {
                        double value = getNumberInput("Enter value to store: ");
                        calculator.memoryStore(value);
                        cout << "Value " << value << " stored in memory." << endl;
                        break;
                    }
                    case 2: {
                        double value = calculator.memoryRecall();
                        cout << "Memory value: " << value << endl;
                        break;
                    }
                    case 3: {
                        calculator.memoryClear();
                        cout << "Memory cleared." << endl;
                        break;
                    }
                    case 4: {
                        double value = getNumberInput("Enter value to add: ");
                        calculator.memoryAdd(value);
                        cout << "Value added to memory." << endl;
                        break;
                    }
                    case 5: {
                        double value = getNumberInput("Enter value to subtract: ");
                        calculator.memorySubtract(value);
                        cout << "Value subtracted from memory." << endl;
                        break;
                    }
                    default: cout << "Invalid operation choice!" << endl;
                }
            }
            catch (const exception& e) {
                throw;
            }
        }
    };
}

int main() {
    ScientificCalculator::ScientificCalcUI calcUI;
    calcUI.run();
    return 0;
}
\`\`\`
`,
    tags: ["C++", "Homework", "Scientific Calculator", "Trigonometry", "Logarithms", "Statistics", "Memory Operations", "Math Functions"],
    date: "2024-10-22",
  },
  {
    id: 1022,
    topic: "c++",
    seriesKey: "c++",
    title: "Homework",
    description: "Implementing comprehensive input validation and error handling mechanisms.",
    content: `
## 🛡️ Homework: Robust Input Validation and Error Handling
Implementing comprehensive input validation and error handling for robust applications.

### Advanced Input Validation System
\`\`\`cpp
#include <iostream>
#include <string>
#include <vector>
#include <regex>
#include <limits>
#include <stdexcept>
#include <sstream>
#include <iomanip>
using namespace std;

namespace InputValidation {
    // Custom validation exceptions
    class ValidationException : public exception {
    private:
        string message;
    public:
        ValidationException(const string& msg) : message(msg) {}
        const char* what() const noexcept override { return message.c_str(); }
    };
    
    class InputValidator {
    public:
        // Basic input validation
        static int getInteger(const string& prompt, int min = INT_MIN, int max = INT_MAX) {
            int value;
            while (true) {
                cout << prompt;
                cin >> value;
                
                if (cin.fail()) {
                    cin.clear();
                    cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                    cout << "Invalid input! Please enter a valid integer." << endl;
                    continue;
                }
                
                cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                
                if (value < min || value > max) {
                    cout << "Value must be between " << min << " and " << max << "!" << endl;
                    continue;
                }
                
                return value;
            }
        }
        
        static double getDouble(const string& prompt, double min = -1e308, double max = 1e308) {
            double value;
            while (true) {
                cout << prompt;
                cin >> value;
                
                if (cin.fail()) {
                    cin.clear();
                    cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                    cout << "Invalid input! Please enter a valid number." << endl;
                    continue;
                }
                
                cin.ignore(numeric_limits<streamsize>::max(), '\\n');
                
                if (value < min || value > max) {
                    cout << "Value must be between " << min << " and " << max << "!" << endl;
                    continue;
                }
                
                return value;
            }
        }
        
        static string getString(const string& prompt, size_t minLength = 0, size_t maxLength = 1000) {
            string value;
            while (true) {
                cout << prompt;
                getline(cin, value);
                
                if (value.length() < minLength) {
                    cout << "Input must be at least " << minLength << " characters long!" << endl;
                    continue;
                }
                
                if (value.length() > maxLength) {
                    cout << "Input cannot exceed " << maxLength << " characters!" << endl;
                    continue;
                }
                
                return value;
            }
        }
        
        // Advanced validation with regex
        static string getEmail(const string& prompt) {
            regex emailPattern(R"(^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$)");
            string email;
            
            while (true) {
                email = getString(prompt, 5, 254);
                
                if (regex_match(email, emailPattern)) {
                    return email;
                } else {
                    cout << "Invalid email format! Please enter a valid email address." << endl;
                }
            }
        }
        
        static string getPhoneNumber(const string& prompt) {
            regex phonePattern(R"(^\\+?[0-9]{10,15}$)");
            string phone;
            
            while (true) {
                phone = getString(prompt, 10, 15);
                
                // Remove any non-digit characters except leading +
                string cleaned;
                for (char c : phone) {
                    if (isdigit(c) || (cleaned.empty() && c == '+')) {
                        cleaned += c;
                    }
                }
                
                if (regex_match(cleaned, phonePattern)) {
                    return cleaned;
                } else {
                    cout << "Invalid phone number format! Please enter 10-15 digits." << endl;
                }
            }
        }
        
        static string getDate(const string& prompt) {
            regex datePattern(R"(^\\d{4}-\\d{2}-\\d{2}$)");
            string date;
            
            while (true) {
                date = getString(prompt, 10, 10);
                
                if (regex_match(date, datePattern)) {
                    // Basic date validation
                    int year = stoi(date.substr(0, 4));
                    int month = stoi(date.substr(5, 2));
                    int day = stoi(date.substr(8, 2));
                    
                    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
                        return date;
                    }
                }
                
                cout << "Invalid date format! Please use YYYY-MM-DD format." << endl;
            }
        }
        
        // Menu selection validation
        static int getMenuChoice(const string& prompt, int minChoice, int maxChoice) {
            return getInteger(prompt, minChoice, maxChoice);
        }
        
        // Yes/No validation
        static bool getYesNo(const string& prompt) {
            while (true) {
                string response = getString(prompt, 1, 3);
                transform(response.begin(), response.end(), response.begin(), ::tolower);
                
                if (response == "y" || response == "yes" || response == "1") {
                    return true;
                } else if (response == "n" || response == "no" || response == "0") {
                    return false;
                } else {
                    cout << "Please enter 'y' for yes or 'n' for no." << endl;
                }
            }
        }
        
        // Password validation
        static string getPassword(const string& prompt) {
            regex passwordPattern(R"(^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$)");
            string password;
            
            while (true) {
                cout << prompt;
                // For password input, you might want to hide the input
                // This is a simplified version
                getline(cin, password);
                
                if (regex_match(password, passwordPattern)) {
                    return password;
                } else {
                    cout << "Password must contain:" << endl;
                    cout << "- At least 8 characters" << endl;
                    cout << "- At least one uppercase letter" << endl;
                    cout << "- At least one lowercase letter" << endl;
                    cout << "- At least one number" << endl;
                    cout << "- At least one special character (@$!%*?&)" << endl;
                }
            }
        }
    };
    
    // Demonstration application
    class UserRegistrationSystem {
    private:
        struct User {
            string username;
            string email;
            string phone;
            string password;
            string birthDate;
            int age;
            
            void display() const {
                cout << "\\n=== USER PROFILE ===" << endl;
                cout << "Username: " << username << endl;
                cout << "Email: " << email << endl;
                cout << "Phone: " << phone << endl;
                cout << "Birth Date: " << birthDate << endl;
                cout << "Age: " << age << endl;
                cout << "====================" << endl;
            }
        };
        
        vector<User> users;
        
    public:
        void run() {
            cout << "=== USER REGISTRATION SYSTEM ===" << endl;
            cout << "Demonstrating robust input validation" << endl;
            
            while (true) {
                cout << "\\n1. Register New User" << endl;
                cout << "2. Display All Users" << endl;
                cout << "3. Exit" << endl;
                
                int choice = InputValidator::getMenuChoice("Enter choice: ", 1, 3);
                
                switch (choice) {
                    case 1: registerUser(); break;
                    case 2: displayUsers(); break;
                    case 3: 
                        cout << "Thank you for using the registration system!" << endl;
                        return;
                }
            }
        }
        
    private:
        void registerUser() {
            User newUser;
            
            cout << "\\n=== NEW USER REGISTRATION ===" << endl;
            
            // Username validation
            newUser.username = InputValidator::getString(
                "Enter username (3-20 characters): ", 3, 20);
            
            // Email validation
            newUser.email = InputValidator::getEmail("Enter email: ");
            
            // Phone validation
            newUser.phone = InputValidator::getPhoneNumber("Enter phone number: ");
            
            // Password validation
            newUser.password = InputValidator::getPassword("Enter password: ");
            
            // Date validation
            newUser.birthDate = InputValidator::getDate("Enter birth date (YYYY-MM-DD): ");
            
            // Age validation
            newUser.age = InputValidator::getInteger("Enter age: ", 0, 150);
            
            users.push_back(newUser);
            cout << "\\nUser registered successfully!" << endl;
            newUser.display();
        }
        
        void displayUsers() const {
            if (users.empty()) {
                cout << "No users registered yet!" << endl;
                return;
            }
            
            cout << "\\n=== REGISTERED USERS ===" << endl;
            for (size_t i = 0; i < users.size(); i++) {
                cout << "User #" << i + 1 << ":" << endl;
                users[i].display();
            }
        }
    };
}

// File validation example
namespace FileValidation {
    class FileValidator {
    public:
        static bool validateFileExtension(const string& filename, const vector<string>& allowedExtensions) {
            size_t dotPos = filename.find_last_of('.');
            if (dotPos == string::npos) {
                return false;
            }
            
            string extension = filename.substr(dotPos + 1);
            transform(extension.begin(), extension.end(), extension.begin(), ::tolower);
            
            for (const auto& allowedExt : allowedExtensions) {
                if (extension == allowedExt) {
                    return true;
                }
            }
            
            return false;
        }
        
        static bool validateFileSize(const string& filename, size_t maxSizeBytes) {
            // Simulated file size validation
            // In real implementation, you would use filesystem library
            return filename.length() * 10 <= maxSizeBytes; // Simple simulation
        }
        
        static string getValidFilename(const string& prompt, const vector<string>& allowedExtensions, size_t maxSize = 10 * 1024 * 1024) {
            string filename;
            
            while (true) {
                filename = InputValidation::InputValidator::getString(prompt, 1, 255);
                
                if (!validateFileExtension(filename, allowedExtensions)) {
                    cout << "Invalid file extension! Allowed: ";
                    for (size_t i = 0; i < allowedExtensions.size(); i++) {
                        cout << allowedExtensions[i];
                        if (i < allowedExtensions.size() - 1) cout << ", ";
                    }
                    cout << endl;
                    continue;
                }
                
                if (!validateFileSize(filename, maxSize)) {
                    cout << "File too large! Maximum size: " << maxSize / (1024 * 1024) << " MB" << endl;
                    continue;
                }
                
                return filename;
            }
        }
    };
}

// Data integrity validation
namespace DataIntegrity {
    class DataValidator {
    public:
        static bool validateCreditCard(const string& cardNumber) {
            // Luhn algorithm implementation
            int sum = 0;
            bool alternate = false;
            
            for (int i = cardNumber.length() - 1; i >= 0; i--) {
                int digit = cardNumber[i] - '0';
                
                if (alternate) {
                    digit *= 2;
                    if (digit > 9) {
                        digit = (digit % 10) + 1;
                    }
                }
                
                sum += digit;
                alternate = !alternate;
            }
            
            return (sum % 10 == 0);
        }
        
        static bool validateISBN(const string& isbn) {
            // Simple ISBN-10 validation
            if (isbn.length() != 10) return false;
            
            int sum = 0;
            for (int i = 0; i < 10; i++) {
                int digit;
                if (i == 9 && isbn[i] == 'X') {
                    digit = 10;
                } else {
                    digit = isbn[i] - '0';
                }
                sum += digit * (10 - i);
            }
            
            return (sum % 11 == 0);
        }
        
        static string getValidCreditCard(const string& prompt) {
            string cardNumber;
            regex cardPattern(R"(^\\d{13,19}$)");
            
            while (true) {
                cardNumber = InputValidation::InputValidator::getString(prompt, 13, 19);
                
                // Remove any non-digit characters
                string cleaned;
                for (char c : cardNumber) {
                    if (isdigit(c)) {
                        cleaned += c;
                    }
                }
                
                if (!regex_match(cleaned, cardPattern)) {
                    cout << "Invalid card number length! Must be 13-19 digits." << endl;
                    continue;
                }
                
                if (!validateCreditCard(cleaned)) {
                    cout << "Invalid card number! Failed checksum validation." << endl;
                    continue;
                }
                
                return cleaned;
            }
        }
    };
}

int main() {
    // Run user registration system demonstration
    InputValidation::UserRegistrationSystem registrationSystem;
    registrationSystem.run();
    
    // Additional validation examples
    cout << "\\n=== ADDITIONAL VALIDATION EXAMPLES ===" << endl;
    
    // File validation example
    vector<string> allowedExtensions = {"txt", "pdf", "doc", "jpg"};
    string filename = FileValidation::FileValidator::getValidFilename(
        "Enter filename: ", allowedExtensions, 5 * 1024 * 1024);
    cout << "Valid filename: " << filename << endl;
    
    // Credit card validation example
    string cardNumber = DataIntegrity::DataValidator::getValidCreditCard(
        "Enter credit card number: ");
    cout << "Valid credit card: " << cardNumber << endl;
    
    return 0;
}
\`\`\`
`,
    tags: ["C++", "Homework", "Input Validation", "Error Handling", "Regex", "Data Validation", "Exception Handling", "Robust Programming"],
    date: "2024-10-23",
  }
];
